/**
 * ConnectX API Layer — powered by Supabase
 *
 * Keeps the same public interface (types + function signatures) so existing
 * pages/components continue to work with minimal import changes.
 */
import { supabase } from '../lib/supabaseClient';
import {
  signIn as authSignIn,
  signUp as authSignUp,
  signOut as authSignOut,
  signInWithGoogle as authSignInWithGoogle,
  resetPasswordRequest,
  updatePassword,
  getSession,
  getProfile,
  ensureProfileForAuthUser,
  updateProfile as authUpdateProfile,
  resendVerificationEmail,
  type GoogleOAuthIntent,
} from './auth';
import type {
  Profile,
  SuccessStory,
  JobApplicationStatus,
  JobStatus,
  JobApplicantView,
  ExternalJob,
} from '../types/database.types';
import { cleanExtractedName, cleanExtractedText } from '../utils/formatters';
import { logEvent } from './eventLogger';
import {
  buildCacheKey,
  cachedFetch,
  clearApiCache,
  invalidateCacheKey,
  invalidateCachePrefix,
} from './apiCache';

// ─── Types (kept for backward compatibility) ─────────────

export interface UserRegistrationData {
  full_name: string;
  primary_email: string;
  office_email?: string;
  mobile_number?: string;
  designation?: string;
  company_name?: string;
  location?: string;
  password: string;
  role?: 'job_seeker' | 'referrer';
  consented_at?: string;
  consent_version?: string;
  is_adult?: boolean;
}

export interface LoginData {
  email: string;
  password: string;
}

export interface UserProfile {
  id: string;
  full_name: string;
  primary_email: string;
  office_email?: string;
  mobile_number?: string;
  designation?: string;
  company_name?: string;
  location?: string;
  bio?: string;
  role: 'job_seeker' | 'referrer' | 'admin';
  auth_provider: string;
  is_active: boolean;
  is_verified: boolean;
  email_verified: boolean;
  profile_picture?: string;
  linkedin_url?: string;
  created_at: string;
  updated_at?: string;
  last_login?: string;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
  status: number;
}

export interface UserResponse {
  id: string;
  full_name: string;
  primary_email: string;
  office_email?: string;
  mobile_number?: string;
  designation?: string;
  company_name?: string;
  location?: string;
  role: string;
  is_active: boolean;
  is_verified: boolean;
  email_verified: boolean;
  profile_picture?: string;
  linkedin_url?: string;
  created_at: string;
}

export interface AuthTokens {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
}

export interface SecureUserInfo {
  full_name: string;
  designation?: string;
  message: string;
}

export interface LoginResponse {
  user: SecureUserInfo;
  tokens: AuthTokens;
}

export interface GoogleAuthUser {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
  provider: string;
}

export interface GoogleAuthResponse {
  token: string;
  user: GoogleAuthUser;
  requires_signup?: boolean;
  email?: string;
  first_name?: string | null;
  last_name?: string | null;
}

// Job and Category Types
export interface Category {
  id: number;
  name: string;
  description?: string;
  icon?: string;
  is_active: boolean;
  created_at: string;
  updated_at?: string;
  job_count?: number;
}

export interface Job {
  id: number;
  title: string;
  description: string;
  company_name: string;
  location: string;
  job_type: 'full_time' | 'part_time' | 'contract' | 'internship' | 'freelance';
  experience_level: 'entry' | 'mid' | 'senior' | 'executive';
  work_mode: 'remote' | 'onsite' | 'hybrid';
  status: 'active' | 'closed' | 'draft' | 'paused';
  salary_min?: number;
  salary_max?: number;
  currency: string;
  skills_required?: string;
  experience_years: number;
  contact_email?: string;
  application_url?: string;
  is_featured: boolean;
  views_count: number;
  applications_count: number;
  requirements?: string;
  benefits?: string;
  created_at: string;
  updated_at?: string;
  expires_at?: string;
  category_id: number;
  posted_by: string;
  category?: Category;
  poster_name?: string;
}

export interface JobListResponse {
  jobs: Job[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
  has_next: boolean;
  has_prev: boolean;
}

export interface JobFilters {
  category_id?: number;
  job_type?: string;
  experience_level?: string;
  work_mode?: string;
  location?: string;
  company_name?: string;
  salary_min?: number;
  salary_max?: number;
  is_featured?: boolean;
  search_query?: string;
}

export interface JobCreate {
  title: string;
  description: string;
  company_name: string;
  location: string;
  job_type: string;
  experience_level: string;
  work_mode: string;
  salary_min?: number;
  salary_max?: number;
  currency?: string;
  skills_required?: string;
  experience_years?: number;
  contact_email?: string;
  application_url?: string;
  expires_at?: string;
  category_id: number;
  requirements?: string;
  benefits?: string;
  is_featured?: boolean;
}

export interface CategoryStats {
  category_id: number;
  category_name: string;
  total_jobs: number;
  active_jobs: number;
  featured_jobs: number;
  avg_salary?: number;
}

export interface JobStats {
  total_jobs: number;
  active_jobs: number;
  featured_jobs: number;
  total_categories: number;
  total_applications: number;
  avg_salary?: number;
}

/** A row in public.job_applications (see migration 067). */
export interface JobApplicationRecord {
  id: number;
  job_id: number;
  user_id: string;
  status: JobApplicationStatus;
  cover_note: string | null;
  created_at: string;
  updated_at: string;
}

/** A seeker's application joined to the job it targets (for the "My Applications" view). */
export interface MyJobApplication extends JobApplicationRecord {
  job?: Job | null;
}

/** One applicant to a posting, from the `get_job_applicants` RPC. */
export type JobApplicant = JobApplicantView;

export interface OnboardingPayload {
  aadhaar_number?: string;
  pan_number?: string;
  official_email?: string;
  github_link?: string;
  linkedin_profile_link?: string;
  country: string;
  state: string;
  city: string;
  contact_number: string;
  company_name?: string;
  company_email?: string;
  /** From onboarding step 1; maps to `profiles.role` (referrer vs job_seeker). */
  user_type?: 'working_professional' | 'job_seeker';
  consented_at?: string;
  consent_version?: string;
  is_adult?: boolean;
}

export interface ReferralRequestPayload {
  referrer_id?: string;
  referrer_name: string;
  referrer_company: string;
  referrer_title?: string;
  job_url?: string;
  job_title?: string;
  role_type?: string;
  message_template?: string;
  personal_message: string;
  technical_skills?: string;
  experience_level?: string;
  priority?: string;
  resume_file_name?: string;
  resume_file?: File;
  resume_file_path?: string;
  preferred_day?: string;
  preferred_time?: string;
  preferred_timezone?: string;
  requested_duration_minutes?: number;
  /** payment_orders.id proving this booking was paid for. Required once payment_settings.enforce_payment_gate is on. */
  payment_order_id?: string;
}

export type ResumeReviewStatus = 'pending' | 'feedback_delivered' | 'cancelled';

export interface ResumeReviewRequestPayload {
  referrer_id?: string;
  referrer_name: string;
  referrer_company: string;
  referrer_title?: string;
  target_role?: string;
  focus_note?: string;
  jd_url?: string;
  resume_file: File;
  /** payment_orders.id proving this booking was paid for. Required once payment_settings.enforce_payment_gate is on. */
  payment_order_id?: string;
}

export interface ResumeReviewRequestRecord {
  id: number;
  requester_id: string;
  requester_name: string;
  referrer_id: string | null;
  referrer_name: string;
  referrer_company: string;
  referrer_title: string | null;
  target_role?: string | null;
  focus_note: string | null;
  jd_url: string | null;
  resume_file_path: string;
  status: ResumeReviewStatus;
  feedback_text: string | null;
  feedback_submitted_at: string | null;
  is_rated: boolean;
  gross_amount: number | null;
  created_at: string;
  updated_at: string;
  resume_review_ratings?: any[];
}

export interface ResumeReviewRequestSummary {
  request: ResumeReviewRequestRecord;
  resume_url: string | null;
  requester_profile: Profile | null;
  referrer_profile?: Profile | null;
}

export type ReferralSessionStatus = 'scheduled' | 'rescheduled' | 'cancelled' | 'completed' | 'expired';

export interface ReferralSessionRecord {
  id: number;
  request_id: number | null;
  requester_id: string;
  referrer_id: string;
  referrer_name: string | null;
  referrer_company: string | null;
  status: ReferralSessionStatus;
  scheduled_start: string;
  scheduled_end: string;
  duration_minutes: number;
  reschedule_count: number;
  cancelled_at: string | null;
  cancelled_by: string | null;
  cancel_reason: string | null;
  daily_room_name: string | null;
  daily_room_url: string | null;
  created_at: string;
  updated_at: string;
  is_rated: boolean;
  gross_amount: number | null;
  requester_profile?: Profile | null;
  referrer_profile?: Profile | null;
  referral_requests?: {
    resume_file_name: string | null;
    resume_file_path: string | null;
  } | null;
  referral_session_ratings?: any[];
}

export interface ReferralSessionSchedulePayload {
  request_id: number | null;
  requester_id: string;
  referrer_id: string;
  referrer_name?: string | null;
  referrer_company?: string | null;
  scheduled_start: string;
  scheduled_end: string;
  duration_minutes: number;
}

export interface MockInterviewRequestPayload {
  referrer_id?: string;
  referrer_name: string;
  referrer_company: string;
  referrer_title?: string;
  company_question?: string;
  focus_note?: string;
  preferred_day?: string;
  preferred_time?: string;
  preferred_timezone?: string;
  requested_duration_minutes?: number;
  price?: number;
  /** payment_orders.id proving this booking was paid for. Required once payment_settings.enforce_payment_gate is on. */
  payment_order_id?: string;
}

export type MockInterviewSessionStatus = 'scheduled' | 'rescheduled' | 'cancelled' | 'completed' | 'expired';

export interface MockInterviewSessionRecord {
  id: number;
  request_id: number | null;
  requester_id: string;
  referrer_id: string;
  referrer_name: string | null;
  referrer_company: string | null;
  status: MockInterviewSessionStatus;
  scheduled_start: string;
  scheduled_end: string;
  duration_minutes: number;
  reschedule_count: number;
  cancelled_at: string | null;
  cancelled_by: string | null;
  cancel_reason: string | null;
  daily_room_name: string | null;
  daily_room_url: string | null;
  created_at: string;
  updated_at: string;
  is_rated: boolean;
  gross_amount: number | null;
  requester_profile?: Profile | null;
  referrer_profile?: Profile | null;
  mock_interview_session_ratings?: any[];
}

/**
 * Minimized availability window returned by `get_referrer_busy_slots`.
 *
 * Deliberately carries no identity, room URL, or price so that checking a
 * professional's calendar never requires read access to their session rows.
 */
export interface BusySlot {
  service_id: 'referral-session' | 'culture-call';
  scheduled_start: string;
  scheduled_end: string;
  duration_minutes: number;
  status: string;
}

export interface MockInterviewSessionSchedulePayload {
  request_id: number | null;
  requester_id: string;
  referrer_id: string;
  referrer_name?: string | null;
  referrer_company?: string | null;
  scheduled_start: string;
  scheduled_end: string;
  duration_minutes: number;
}

// ─── Shared request notes (both parties) ────────────────

/** Which request table a note thread hangs off. */
export type RequestNoteService = 'referral' | 'resume_review' | 'mock_interview';

export interface RequestNoteRecord {
  id: number;
  service: RequestNoteService;
  request_id: number;
  author_id: string;
  body: string;
  is_action_item: boolean;
  is_done: boolean;
  created_at: string;
  updated_at: string;
  /** Joined client-side so the thread can show who wrote what. */
  author_profile?: Profile | null;
}

// ─── Helper: wrap async calls into ApiResponse ───────────

/** User-facing copy for Supabase GoTrue / PostgREST limits (see Dashboard → Authentication → Rate Limits). */
function formatSupabaseClientError(err: unknown): string {
  const e = err as { message?: string; code?: string; status?: number };
  const code = String(e?.code || '');
  const msg = String(e?.message || '').toLowerCase();

  if (code === 'over_email_send_rate_limit' || (msg.includes('email') && msg.includes('rate limit'))) {
    return 'Too many emails were sent from this app. Wait a few minutes, check your inbox for a message you already received, or ask your admin to raise Auth rate limits in Supabase (Authentication → Rate Limits).';
  }
  if (code === 'over_request_rate_limit' || e?.status === 429 || msg.includes('rate_limited') || msg.includes('too many attempts') || msg.includes('rate limit')) {
    return 'Too many attempts. Please wait a minute and try again.';
  }
  return e?.message || 'An error occurred';
}

const signedUrlCache = new Map<string, { url: string; expiresAt: number }>();

export async function getSafeSignedResumeUrl(bucket: string, path: string): Promise<string | null> {
  if (!path) return null;
  if (path.startsWith('http://') || path.startsWith('https://')) return path;

  const cacheKey = `${bucket}:${path}`;
  const cached = signedUrlCache.get(cacheKey);
  if (cached && cached.expiresAt > Date.now()) {
    return cached.url;
  }

  // Attempt 1: createSignedUrl
  try {
    const { data, error } = await supabase.storage.from(bucket).createSignedUrl(path, 3600);
    if (!error && data?.signedUrl) {
      signedUrlCache.set(cacheKey, { url: data.signedUrl, expiresAt: Date.now() + 3000 * 1000 });
      return data.signedUrl;
    }
  } catch {
    /* silent storage timeout fallback */
  }

  // Attempt 2: Retry createSignedUrl once after 300ms delay if gateway timed out (e.g. HTTP 544)
  try {
    await new Promise((resolve) => setTimeout(resolve, 300));
    const { data: retryData, error: retryError } = await supabase.storage.from(bucket).createSignedUrl(path, 3600);
    if (!retryError && retryData?.signedUrl) {
      signedUrlCache.set(cacheKey, { url: retryData.signedUrl, expiresAt: Date.now() + 3000 * 1000 });
      return retryData.signedUrl;
    }
  } catch {
    /* silent storage retry timeout */
  }

  // Fallback to public URL if signed URL times out (e.g. HTTP 544 gateway timeout)
  try {
    const { data: publicData } = supabase.storage.from(bucket).getPublicUrl(path);
    if (publicData?.publicUrl) {
      signedUrlCache.set(cacheKey, { url: publicData.publicUrl, expiresAt: Date.now() + 600 * 1000 });
      return publicData.publicUrl;
    }
  } catch {
    /* silent fallback */
  }

  return null;
}

/**
 * Pulls the real message out of a failed `functions.invoke` call.
 *
 * Supabase surfaces a non-2xx edge function response as a FunctionsHttpError
 * whose `message` is just "Edge Function returned a non-2xx status code". The
 * useful text lives in the untouched Response on `context`, so read it there —
 * otherwise users see a generic error instead of "Payment order has already
 * been used" or "Too many payment attempts".
 */
async function readFunctionError(err: any, fallback: string): Promise<string> {
  try {
    const body = await err?.context?.json?.();
    if (body?.error) return String(body.error);
  } catch {
    /* response already consumed or not JSON */
  }
  return err?.message && !/non-2xx/i.test(err.message) ? err.message : fallback;
}

async function wrapAsync<T>(
  fn: () => Promise<T>,
  cache?: { key: string; ttlMs?: number }
): Promise<ApiResponse<T>> {
  try {
    const data = cache
      ? await cachedFetch(cache.key, fn, cache.ttlMs)
      : await fn();
    return { success: true, data, status: 200 };
  } catch (err: any) {
    return {
      success: false,
      error: formatSupabaseClientError(err),
      status: err?.status || 500,
    };
  }
}

/**
 * Prices the platform sets and professionals cannot change.
 * Mirrors `payment_settings.default_price_inr` (migration 057). Referral
 * sessions and mock interviews are deliberately absent — those are set by the
 * professional on the My Services page, and the server refuses to open a
 * checkout for an unpriced one rather than inventing an amount.
 */
export const PLATFORM_FIXED_SERVICE_PRICES: Record<string, number> = {
  'resume-review': 299,
};

/** Bounds a professional may price within. Mirrors useServicesManager. */
export const MIN_SERVICE_PRICE = 500;
export const MAX_SERVICE_PRICE = 3000;

function isDevAuthBypassEnabled(): boolean {
  // Dev bypass removed: always require real Supabase auth in all environments.
  // This ensures there are no fake profiles or local-only bypasses left enabled.
  return false;
}

function sanitizeFileName(fileName: string): string {
  return fileName.replace(/[^a-zA-Z0-9._-]+/g, '_');
}

/**
 * Public profile projection: explicitly excludes sensitive PII
 * (primary_email, office_email, mobile_number, google_sub, billing/bank info).
 */
export const PUBLIC_PROFILE_SELECT = `
  id,
  full_name,
  designation,
  company_name,
  location,
  role,
  is_active,
  is_verified,
  profile_picture,
  linkedin_url,
  github_link,
  portfolio_url,
  bio,
  skills,
  industry,
  languages,
  years_of_experience,
  work_modes,
  notice_period,
  availability_status,
  preferred_locations,
  education_entries,
  project_entries,
  certification_entries,
  work_history_entries,
  achievement_entries,
  hobbies,
  user_code,
  verified_company_domain,
  created_at
`.replace(/\s+/g, ' ').trim();

/**
 * Service participant profile projection: Used for active booked services
 * (referrals, resume reviews, mock interviews, sessions). Preserves participant email
 * and professional details for the service while strictly keeping mobile_number
 * and google_sub private.
 */
export const SERVICE_PARTICIPANT_PROFILE_SELECT = `
  id,
  full_name,
  primary_email,
  office_email,
  designation,
  company_name,
  location,
  role,
  is_active,
  is_verified,
  profile_picture,
  linkedin_url,
  github_link,
  portfolio_url,
  bio,
  skills,
  industry,
  languages,
  years_of_experience,
  work_modes,
  notice_period,
  availability_status,
  preferred_locations,
  education_entries,
  project_entries,
  certification_entries,
  work_history_entries,
  achievement_entries,
  hobbies,
  user_code,
  verified_company_domain,
  created_at
`.replace(/\s+/g, ' ').trim();

// ─── API Client (Supabase-powered) ───────────────────────

class ApiClient {
  // Kept for backward compatibility — no-ops since Supabase manages tokens
  setAuthToken(_token: string) { /* no-op */ }
  clearAuthToken() { /* no-op */ }

  // ── Auth ─────────────────────────────────────────────

  async register(userData: UserRegistrationData): Promise<ApiResponse<LoginResponse>> {
    return wrapAsync(async () => {
      const consentData = userData.consented_at ? {
        consentedAt: userData.consented_at,
        consentVersion: userData.consent_version || 'v1.0',
        isAdult: userData.is_adult || false
      } : undefined;

      const result = await authSignUp(
        userData.primary_email,
        userData.password,
        userData.full_name,
        userData.role || 'job_seeker',
        consentData
      );

      const session = result.session;
      const user = result.user;

      // Store tokens for backward compatibility with ProtectedRoute
      if (session) {
        localStorage.setItem('access_token', session.access_token);
        localStorage.setItem('refresh_token', session.refresh_token);
      }

      return {
        user: {
          full_name: user?.user_metadata?.full_name || userData.full_name,
          designation: undefined,
          message: 'Registration successful',
        },
        tokens: {
          access_token: session?.access_token || '',
          refresh_token: session?.refresh_token || '',
          token_type: 'bearer',
          expires_in: session?.expires_in || 3600,
        },
      };
    });
  }

  async login(loginData: LoginData): Promise<ApiResponse<LoginResponse>> {
    return wrapAsync(async () => {
      const result = await authSignIn(loginData.email, loginData.password);
      const session = result.session;
      const user = result.user;

      if (session) {
        localStorage.setItem('access_token', session.access_token);
        localStorage.setItem('refresh_token', session.refresh_token);
      }

      // Fetch profile for full_name
      const profile = user ? await getProfile(user.id) : null;

      return {
        user: {
          full_name: profile?.full_name || user?.user_metadata?.full_name || 'User',
          designation: profile?.designation || undefined,
          message: 'Login successful',
        },
        tokens: {
          access_token: session?.access_token || '',
          refresh_token: session?.refresh_token || '',
          token_type: 'bearer',
          expires_in: session?.expires_in || 3600,
        },
      };
    });
  }

  async resendVerification(email: string): Promise<ApiResponse<{ message: string }>> {
    return wrapAsync(async () => {
      await resendVerificationEmail(email);
      return { message: 'Verification email sent successfully' };
    });
  }

  async forgotPassword(email: string): Promise<ApiResponse<{ message: string; email_exists: boolean }>> {
    return wrapAsync(async () => {
      await resetPasswordRequest(email);
      return { message: 'Password reset email sent', email_exists: true };
    });
  }

  // Obfuscate password using simple encoding
  private obfuscatePassword(password: string): string {
    // Simple obfuscation: base64 encode with salt
    const salt = 'Reffconnect_Salt_2024';
    const saltedPassword = password + salt;
    return btoa(saltedPassword);
  }

  async refreshToken(refreshToken: string): Promise<ApiResponse<AuthTokens>> {
    return wrapAsync(async () => {
      const { data, error } = await supabase.auth.refreshSession({ refresh_token: refreshToken });
      if (error) throw error;
      const session = data.session;
      return {
        access_token: session?.access_token || '',
        refresh_token: session?.refresh_token || '',
        token_type: 'bearer',
        expires_in: session?.expires_in || 3600,
      };
    });
  }

  async verifyEmail(_token: string): Promise<ApiResponse<{ message: string }>> {
    return wrapAsync(async () => {
      return { message: 'Email verified via Supabase' };
    });
  }

  // ── Google OAuth ─────────────────────────────────────

  async getGoogleAuthUrl(intent: GoogleOAuthIntent = 'signup'): Promise<ApiResponse<{ auth_url: string }>> {
    return wrapAsync(async () => {
      const result = await authSignInWithGoogle({ intent });
      return { auth_url: result.url || '' };
    });
  }

  async googleSignIn(_accessToken: string): Promise<ApiResponse<GoogleAuthResponse>> {
    // With Supabase, Google OAuth is handled via redirect, not token exchange
    return wrapAsync(async () => {
      await authSignInWithGoogle();
      return {} as GoogleAuthResponse;
    });
  }

  async googleSignUp(_accessToken: string): Promise<ApiResponse<GoogleAuthResponse>> {
    return this.googleSignIn(_accessToken);
  }

  async googleOAuthToken(_accessToken: string): Promise<ApiResponse<LoginResponse>> {
    return wrapAsync(async () => {
      return {} as LoginResponse;
    });
  }

  async googleOAuthCallback(_code: string): Promise<ApiResponse<LoginResponse>> {
    return wrapAsync(async () => {
      return {} as LoginResponse;
    });
  }

  // ── User ─────────────────────────────────────────────

  async getCurrentUser(): Promise<ApiResponse<UserResponse>> {
    return wrapAsync(async () => {
      const session = await getSession();
      if (!session?.user) throw new Error('Not authenticated');
      const profile = await getProfile(session.user.id);
      if (!profile) throw new Error('Profile not found');
      return profile as unknown as UserResponse;
    });
  }

  async updateUserProfile(userData: Partial<UserRegistrationData>): Promise<ApiResponse<UserResponse>> {
    return wrapAsync(async () => {
      const session = await getSession();
      if (!session?.user) throw new Error('Not authenticated');
      const updated = await authUpdateProfile(session.user.id, userData as any);
      // Invalidate the user-profile cache so subsequent getUserProfile() calls
      // always return fresh data (e.g. newly saved education/project entries).
      invalidateCacheKey(buildCacheKey(['user-profile', session.user.id]));
      // A referrer editing public profile fields (headline, company, social
      // links, work history, etc.) also feeds the marketplace listing, so drop
      // the shared 'referrers' cache + the marketplace base-data cache. Without
      // this the edited card shows stale data until the 60s/20s TTLs expire.
      invalidateCacheKey('referrers');
      try {
        const { invalidateMarketplaceCache } = await import('../../features/bookings/utils/marketplaceCache');
        invalidateMarketplaceCache();
      } catch {
        // Optional marketplace cache invalidation.
      }
      return updated as unknown as UserResponse;
    });
  }

  async changePassword(_currentPassword: string, newPassword: string): Promise<ApiResponse<{ message: string }>> {
    return wrapAsync(async () => {
      await updatePassword(newPassword);
      return { message: 'Password changed successfully' };
    });
  }

  // ── Jobs ─────────────────────────────────────────────

  async getCategories(): Promise<ApiResponse<Category[]>> {
    return wrapAsync(
      async () => {
      // Fetch categories and active job counts in just 2 queries (not N+1)
      const [catResult, jobCountResult] = await Promise.all([
        supabase
          .from('categories')
          .select('*')
          .eq('is_active', true)
          .order('name'),
        supabase
          .from('jobs')
          .select('category_id')
          .eq('status', 'active'),
      ]);

      if (catResult.error) throw catResult.error;
      if (jobCountResult.error) throw jobCountResult.error;

      // Build a count map from the jobs query in O(n)
      const countMap = new Map<number, number>();
      for (const job of jobCountResult.data || []) {
        countMap.set(job.category_id, (countMap.get(job.category_id) || 0) + 1);
      }

      const categories: Category[] = (catResult.data || []).map((cat: any) => ({
        ...cat,
        job_count: countMap.get(cat.id) || 0,
      } as Category));

      return categories;
      },
      { key: 'categories' }
    );
  }

  async getJobs(
    page: number = 1,
    perPage: number = 20,
    filters?: JobFilters
  ): Promise<ApiResponse<JobListResponse>> {
    return wrapAsync(
      async () => {
      let query = supabase
        .from('jobs')
        .select('*, categories(*)', { count: 'exact' });

      // Apply filters
      if (filters?.category_id) query = query.eq('category_id', filters.category_id);
      if (filters?.job_type) query = query.eq('job_type', filters.job_type);
      if (filters?.experience_level) query = query.eq('experience_level', filters.experience_level);
      if (filters?.work_mode) query = query.eq('work_mode', filters.work_mode);
      if (filters?.location) query = query.ilike('location', `%${filters.location}%`);
      if (filters?.company_name) query = query.ilike('company_name', `%${filters.company_name}%`);
      if (filters?.salary_min) query = query.gte('salary_min', filters.salary_min);
      if (filters?.salary_max) query = query.lte('salary_max', filters.salary_max);
      if (filters?.is_featured !== undefined) query = query.eq('is_featured', filters.is_featured);
      if (filters?.search_query) {
        query = query.or(`title.ilike.%${filters.search_query}%,description.ilike.%${filters.search_query}%,company_name.ilike.%${filters.search_query}%`);
      }

      // Default: only active jobs
      query = query.eq('status', 'active');

      // Pagination
      const from = (page - 1) * perPage;
      const to = from + perPage - 1;
      query = query.range(from, to).order('created_at', { ascending: false });

      const { data, error, count } = await query;
      if (error) throw error;

      const total = count || 0;
      const totalPages = Math.ceil(total / perPage);

      // Map category relation
      const jobs = (data || []).map((job: any) => ({
        ...job,
        category: job.categories || undefined,
        categories: undefined,
      }));

      return {
        jobs,
        total,
        page,
        per_page: perPage,
        total_pages: totalPages,
        has_next: page < totalPages,
        has_prev: page > 1,
      };
      },
      { key: buildCacheKey(['jobs', page, perPage, JSON.stringify(filters || {})]) }
    );
  }

  async getJob(jobId: number): Promise<ApiResponse<Job>> {
    return wrapAsync(async () => {
      const { data, error } = await supabase
        .from('jobs')
        .select('*, categories(*)')
        .eq('id', jobId)
        .single();
      if (error) throw error;

      // Increment views
      await supabase.rpc('increment_job_views', { job_id_param: jobId });

      return {
        ...data,
        category: (data as any).categories || undefined,
        categories: undefined,
      } as unknown as Job;
    });
  }

  async createJob(jobData: JobCreate): Promise<ApiResponse<Job>> {
    return wrapAsync(async () => {
      const session = await getSession();
      if (!session?.user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('jobs')
        .insert({
          ...jobData,
          posted_by: session.user.id,
          status: 'active',
          currency: jobData.currency || 'INR',
          experience_years: jobData.experience_years || 0,
        } as any)
        .select('*, categories(*)')
        .single();
      if (error) throw error;

      // Invalidate cached job/category reads so the new posting shows up
      // immediately on the openings page and category counts stay accurate.
      invalidateCachePrefix('jobs');
      invalidateCacheKey('categories');
      invalidateCacheKey('category-stats');
      invalidateCacheKey('job-stats');

      return data as unknown as Job;
    });
  }

  async getJobsByCategory(
    categoryId: number,
    page: number = 1,
    perPage: number = 20
  ): Promise<ApiResponse<JobListResponse>> {
    return this.getJobs(page, perPage, { category_id: categoryId });
  }

  async getCategoryStats(): Promise<ApiResponse<CategoryStats[]>> {
    return wrapAsync(
      async () => {
      // Fetch categories and ALL jobs in just 2 queries (not 3N+1)
      const [catResult, jobsResult] = await Promise.all([
        supabase
          .from('categories')
          .select('id, name')
          .eq('is_active', true),
        supabase
          .from('jobs')
          .select('category_id, status, is_featured'),
      ]);

      if (catResult.error) throw catResult.error;
      if (jobsResult.error) throw jobsResult.error;

      // Build aggregated stats map from the single jobs query in O(n)
      const statsMap = new Map<number, { total: number; active: number; featured: number }>();
      for (const job of jobsResult.data || []) {
        const entry = statsMap.get(job.category_id) || { total: 0, active: 0, featured: 0 };
        entry.total++;
        if (job.status === 'active') entry.active++;
        if (job.is_featured) entry.featured++;
        statsMap.set(job.category_id, entry);
      }

      const stats: CategoryStats[] = (catResult.data || []).map((cat: any) => {
        const counts = statsMap.get(cat.id) || { total: 0, active: 0, featured: 0 };
        return {
          category_id: cat.id,
          category_name: cat.name,
          total_jobs: counts.total,
          active_jobs: counts.active,
          featured_jobs: counts.featured,
        };
      });

      return stats;
      },
      { key: 'category-stats' }
    );
  }

  async getJobStats(): Promise<ApiResponse<JobStats>> {
    return wrapAsync(
      async () => {
        // Try the consolidated single-pass SQL RPC first
        const { data, error } = await supabase.rpc('get_job_stats_summary');
        if (!error && data) {
          const stats = data as Record<string, any>;
          return {
            total_jobs: Number(stats.total_jobs || 0),
            active_jobs: Number(stats.active_jobs || 0),
            featured_jobs: Number(stats.featured_jobs || 0),
            total_categories: Number(stats.total_categories || 0),
            total_applications: Number(stats.total_applications || 0),
          };
        }

        // Fallback to direct parallel counts if RPC is not yet deployed
        const [totalRes, activeRes, featuredRes, catRes, appRes] = await Promise.all([
          supabase.from('jobs').select('*', { count: 'exact', head: true }),
          supabase.from('jobs').select('*', { count: 'exact', head: true }).eq('status', 'active'),
          supabase.from('jobs').select('*', { count: 'exact', head: true }).eq('is_featured', true),
          supabase.from('categories').select('*', { count: 'exact', head: true }).eq('is_active', true),
          supabase.from('job_applications').select('*', { count: 'exact', head: true }),
        ]);

        return {
          total_jobs: totalRes.count || 0,
          active_jobs: activeRes.count || 0,
          featured_jobs: featuredRes.count || 0,
          total_categories: catRes.count || 0,
          total_applications: appRes.count || 0,
        };
      },
      { key: 'job-stats' }
    );
  }

  // ── Job Applications (seeker side) ────────────────────

  /** Apply to a posting. One application per job per user (enforced by a unique index). */
  async applyToJob(jobId: number, coverNote?: string): Promise<ApiResponse<JobApplicationRecord>> {
    return wrapAsync(async () => {
      const session = await getSession();
      if (!session?.user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('job_applications')
        .insert({
          job_id: jobId,
          user_id: session.user.id,
          status: 'applied',
          cover_note: coverNote?.trim() ? coverNote.trim() : null,
        } as any)
        .select('*')
        .single();

      if (error) {
        // 23505 = unique_violation: the seeker already applied to this job.
        if ((error as any).code === '23505') {
          throw new Error('You have already applied to this job.');
        }
        throw error;
      }

      // The new application changes counts on the openings list + the seeker's dashboard.
      invalidateCachePrefix('jobs');
      invalidateCacheKey(buildCacheKey(['jobseeker-dashboard', session.user.id]));

      return data as unknown as JobApplicationRecord;
    });
  }

  /** The current user's application to a given job, or null if they haven't applied. */
  async getMyApplicationForJob(jobId: number): Promise<ApiResponse<JobApplicationRecord | null>> {
    const session = await getSession();
    if (!session?.user) {
      return { success: false, error: 'Not authenticated', status: 401 };
    }
    return wrapAsync(async () => {
      const { data, error } = await supabase
        .from('job_applications')
        .select('*')
        .eq('job_id', jobId)
        .eq('user_id', session.user.id)
        .maybeSingle();
      if (error) throw error;
      return (data as unknown as JobApplicationRecord) || null;
    });
  }

  /** Withdraw the current user's application to a job (deletes the row). */
  async withdrawApplication(jobId: number): Promise<ApiResponse<null>> {
    return wrapAsync(async () => {
      const session = await getSession();
      if (!session?.user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('job_applications')
        .delete()
        .eq('job_id', jobId)
        .eq('user_id', session.user.id);
      if (error) throw error;

      invalidateCachePrefix('jobs');
      invalidateCacheKey(buildCacheKey(['jobseeker-dashboard', session.user.id]));
      return null;
    });
  }

  /** All applications the current user has submitted, newest first, with the job embedded. */
  async getMyJobApplications(): Promise<ApiResponse<MyJobApplication[]>> {
    const session = await getSession();
    if (!session?.user) {
      return { success: false, error: 'Not authenticated', status: 401 };
    }
    return wrapAsync(async () => {
      const { data, error } = await supabase
        .from('job_applications')
        .select('*, jobs(*)')
        .eq('user_id', session.user.id)
        .order('created_at', { ascending: false });
      if (error) throw error;

      return (data || []).map((row: any) => {
        const { jobs, ...application } = row;
        return { ...application, job: jobs || null } as MyJobApplication;
      });
    });
  }

  // ── Job Postings management (professional side) ───────

  /** Every job the current professional has posted (any status), newest first. */
  async getMyPostedJobs(): Promise<ApiResponse<Job[]>> {
    const session = await getSession();
    if (!session?.user) {
      return { success: false, error: 'Not authenticated', status: 401 };
    }
    return wrapAsync(async () => {
      const { data, error } = await supabase
        .from('jobs')
        .select('*, categories(*)')
        .eq('posted_by', session.user.id)
        .order('created_at', { ascending: false });
      if (error) throw error;

      return (data || []).map((job: any) => ({
        ...job,
        category: job.categories || undefined,
        categories: undefined,
      })) as Job[];
    });
  }

  /**
   * Applicants for one of the current professional's postings. Backed by the
   * `get_job_applicants` SECURITY DEFINER RPC, which hard-gates access to the
   * job's owner and returns a public-safe slice of each applicant's profile.
   */
  async getJobApplicants(jobId: number): Promise<ApiResponse<JobApplicant[]>> {
    return wrapAsync(async () => {
      const { data, error } = await supabase.rpc('get_job_applicants', { p_job_id: jobId });
      if (error) throw error;
      return (data || []) as JobApplicant[];
    });
  }

  /** Move an applicant through the pipeline (applied -> reviewing -> shortlisted -> ...). */
  async updateApplicationStatus(
    applicationId: number,
    status: JobApplicationStatus
  ): Promise<ApiResponse<JobApplicationRecord>> {
    return wrapAsync(async () => {
      const { data, error } = await supabase
        .from('job_applications')
        .update({ status })
        .eq('id', applicationId)
        .select('*')
        .single();
      if (error) throw error;

      invalidateCachePrefix('jobs');
      return data as unknown as JobApplicationRecord;
    });
  }

  /** Change a posting's status (e.g. close or re-open it). Owner-scoped. */
  async updateJobStatus(jobId: number, status: JobStatus): Promise<ApiResponse<Job>> {
    return wrapAsync(async () => {
      const session = await getSession();
      if (!session?.user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('jobs')
        .update({ status })
        .eq('id', jobId)
        .eq('posted_by', session.user.id)
        .select('*, categories(*)')
        .single();
      if (error) throw error;

      invalidateCachePrefix('jobs');
      invalidateCacheKey('job-stats');

      return {
        ...(data as any),
        category: (data as any).categories || undefined,
        categories: undefined,
      } as unknown as Job;
    });
  }

  /** Permanently delete a posting (cascades to its applications). Owner-scoped. */
  async deleteJob(jobId: number): Promise<ApiResponse<null>> {
    return wrapAsync(async () => {
      const session = await getSession();
      if (!session?.user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('jobs')
        .delete()
        .eq('id', jobId)
        .eq('posted_by', session.user.id);
      if (error) throw error;

      invalidateCachePrefix('jobs');
      invalidateCacheKey('job-stats');
      invalidateCacheKey('category-stats');
      return null;
    });
  }

  // ── External jobs (ingested from company career pages) ─

  /** Active openings ingested from company ATS/career pages. Optionally scoped to one company. */
  async getExternalJobs(company?: string): Promise<ApiResponse<ExternalJob[]>> {
    return wrapAsync(
      async () => {
        let query = supabase.from('external_jobs').select('*').eq('is_active', true);
        if (company) query = query.ilike('company_name', company);
        const { data, error } = await query
          .order('posted_at', { ascending: false, nullsFirst: false })
          .limit(500);
        if (error) throw error;
        return (data || []) as unknown as ExternalJob[];
      },
      { key: buildCacheKey(['external-jobs', company || 'all']), ttlMs: 60_000 }
    );
  }

  /**
   * Kick off ingestion of a company's (or all onboarded companies') career-page
   * openings via the `ingest-company-jobs` edge function. Fire-and-forget from
   * the client; the function upserts into external_jobs.
   */
  async triggerCompanyJobIngestion(company?: string): Promise<ApiResponse<any>> {
    return wrapAsync(async () => {
      const { data, error } = await supabase.functions.invoke('ingest-company-jobs', {
        body: company ? { company } : {},
      });
      if (error) throw error;
      invalidateCachePrefix('external-jobs');
      return data;
    });
  }

  // ── User Profile ──────────────────────────────────────

  async getUserProfile(): Promise<ApiResponse<UserProfile>> {
    const session = await getSession();
    if (!session?.user) {
      return { success: false, error: 'Not authenticated', status: 401 };
    }
    return wrapAsync(
      async () => {
        let profile = await getProfile(session.user.id);
        if (!profile) throw new Error('Profile not found');

        // Self-heal missing user_code on legacy or un-stamped profiles
        if (!profile.user_code) {
          try {
            const isWP = profile.role === 'referrer' || (profile as any).user_type === 'working_professional';
            const prefix = isWP ? 'REFFR' : 'REEFJS';
            const { data: codeData, error: codeErr } = await supabase
              .rpc('generate_user_code', { p_prefix: prefix });
            if (codeData && !codeErr) {
              await supabase
                .from('profiles')
                .update({ user_code: codeData })
                .eq('id', session.user.id);
              profile.user_code = codeData;
            }
          } catch (codeErr) {
            console.warn('getUserProfile: on-demand user_code assignment skipped', codeErr);
          }
        }

        return profile as unknown as UserProfile;
      },
      { key: buildCacheKey(['user-profile', session.user.id]) }
    );
  }

  async getPlatformFeatures(): Promise<ApiResponse<Array<{ feature_key: string; enabled: boolean }>>> {
    return wrapAsync(
      async () => {
        const { data, error } = await supabase
          .from('platform_features')
          .select('feature_key, enabled');
        if (error) throw error;
        return data || [];
      },
      { key: 'platform-features', ttlMs: 120_000 }
    );
  }

  // ── Dashboard ──────────────────────────────────────────

  async getReferrerDashboard(): Promise<ApiResponse<any>> {
    const session = await getSession();
    if (!session?.user) {
      return { success: false, error: 'Not authenticated', status: 401 };
    }
    return wrapAsync(
      async () => {
        const { data: referrals } = await supabase
          .from('referral_requests')
          .select('*')
          .eq('requester_id', session.user.id);

        return {
          total_referrals: referrals?.length || 0,
          pending_referrals: referrals?.filter((r: any) => r.status === 'pending').length || 0,
        };
      },
      { key: buildCacheKey(['referrer-dashboard', session.user.id]) }
    );
  }

  async getJobseekerDashboard(): Promise<ApiResponse<any>> {
    const session = await getSession();
    if (!session?.user) {
      return { success: false, error: 'Not authenticated', status: 401 };
    }
    return wrapAsync(
      async () => {
        const { data: applications } = await supabase
          .from('job_applications')
          .select('*')
          .eq('user_id', session.user.id);

        return {
          total_applications: applications?.length || 0,
          pending: applications?.filter((a: any) => a.status === 'submitted').length || 0,
        };
      },
      { key: buildCacheKey(['jobseeker-dashboard', session.user.id]) }
    );
  }

  // ── Slot Reservations ─────────────────────────────────────

  async reserveSlot(payload: { referrerId: string; serviceId: string; slotLabel: string }): Promise<ApiResponse<any>> {
    return wrapAsync(async () => {
      const session = await getSession();
      if (!session?.user) throw new Error('Not authenticated');

      if (payload.referrerId === session.user.id) {
        throw new Error('You cannot reserve a slot on your own calendar.');
      }

      const now = new Date().toISOString();
      const expiresAt = new Date(Date.now() + 15 * 60 * 1000).toISOString(); // 15 minutes lock

      // Attempt to upsert the reservation
      const { data: existing, error: fetchError } = await supabase
        .from('slot_reservations')
        .select('*')
        .eq('referrer_id', payload.referrerId)
        .eq('service_id', payload.serviceId)
        .eq('slot_label', payload.slotLabel)
        .eq('is_released', false)
        .gt('expires_at', now)
        .neq('user_id', session.user.id)
        .maybeSingle();

      if (fetchError) throw fetchError;
      if (existing) {
        throw new Error('This slot is temporarily locked by another candidate checkout session.');
      }

      const { data, error } = await supabase
        .from('slot_reservations')
        .upsert({
          user_id: session.user.id,
          referrer_id: payload.referrerId,
          service_id: payload.serviceId,
          slot_label: payload.slotLabel,
          expires_at: expiresAt,
          is_released: false,
          reserved_at: now
        }, {
          onConflict: 'referrer_id,service_id,slot_label'
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    });
  }

  async releaseSlot(payload: { referrerId: string; serviceId: string; slotLabel: string }): Promise<ApiResponse<any>> {
    return wrapAsync(async () => {
      const session = await getSession();
      if (!session?.user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('slot_reservations')
        .update({ is_released: true })
        .eq('referrer_id', payload.referrerId)
        .eq('service_id', payload.serviceId)
        .eq('slot_label', payload.slotLabel)
        .eq('user_id', session.user.id)
        .select();

      if (error) throw error;
      return data;
    });
  }

  async getActiveReservations(referrerId: string): Promise<ApiResponse<any[]>> {
    return wrapAsync(async () => {
      const now = new Date().toISOString();
      const { data, error } = await supabase
        .from('slot_reservations')
        .select('*')
        .eq('referrer_id', referrerId)
        .eq('is_released', false)
        .gt('expires_at', now);

      if (error) throw error;
      return data || [];
    });
  }

  // ── Payments ──────────────────────────────────────────────
  //
  // The browser never sends an amount. `payments-create-order` resolves the
  // price from the referrer's own service_configurations row and hands back a
  // Razorpay order id; Checkout then takes the amount from that order.
  // `payments-verify` HMAC-checks the signature Checkout returns. Neither the
  // key secret nor the fee split is exposed to the client.

  async createPaymentOrder(payload: {
    serviceId: 'referral-session' | 'resume-review' | 'interview-mock';
    referrerId: string;
    slotLabel?: string;
  }): Promise<ApiResponse<{
    paymentOrderId: string;
    razorpayOrderId: string;
    amountPaise: number;
    currency: string;
    keyId: string;
  }>> {
    return wrapAsync(async () => {
      const session = await getSession();
      if (!session?.user) throw new Error('Not authenticated');

      const { data, error } = await supabase.functions.invoke('payments-create-order', {
        body: {
          serviceId: payload.serviceId,
          referrerId: payload.referrerId,
          slotLabel: payload.slotLabel ?? null,
        },
      });

      if (error) throw new Error(await readFunctionError(error, 'Could not start checkout.'));
      if (!data?.razorpayOrderId || !data?.keyId) {
        throw new Error('Checkout could not be initialised. Please try again.');
      }
      return data;
    });
  }

  async verifyPayment(payload: {
    razorpayOrderId: string;
    razorpayPaymentId: string;
    razorpaySignature: string;
  }): Promise<ApiResponse<{ paymentOrderId: string; status: string; amountPaise: number }>> {
    return wrapAsync(async () => {
      const session = await getSession();
      if (!session?.user) throw new Error('Not authenticated');

      const { data, error } = await supabase.functions.invoke('payments-verify', {
        body: payload,
      });

      if (error) throw new Error(await readFunctionError(error, 'Payment verification failed.'));
      if (!data?.paymentOrderId) throw new Error('Payment verification returned no order.');
      return data;
    });
  }

  // ── Onboarding ──────────────────────────────────────────

  async saveOnboarding(payload: OnboardingPayload): Promise<ApiResponse<any>> {
    return wrapAsync(async () => {
      // Use the client directly here to avoid stacking with getSession() retries
      // (AuthContext + onboarding can otherwise contend and appear to hang).
      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();
      if (sessionError) throw sessionError;
      if (!session?.user) throw new Error('Not authenticated');

      await ensureProfileForAuthUser(session.user);

      const isWP = payload.user_type === 'working_professional';

      const { data, error } = await supabase
        .from('user_onboarding')
        .upsert({
          user_id: session.user.id,
          aadhaar_number: payload.aadhaar_number || null,
          pan_number: payload.pan_number || null,
          github_link: payload.github_link || null,
          linkedin_profile_link: payload.linkedin_profile_link || null,
          country: payload.country || null,
          state: payload.state || null,
          city: payload.city || null,
          contact_number: payload.contact_number || null,
          company_name: payload.company_name || null,
          company_email: payload.company_email || null,
          completed_at: new Date().toISOString(),
          user_type: payload.user_type || null,
          // WP accounts require admin review before gaining referrer access
          verification_status: isWP ? 'pending' : null,
        }, { onConflict: 'user_id' })
        .select();
      if (error) throw error;

      // Profile update is best-effort so onboarding can still complete if RLS/columns differ.
      // Working professionals do NOT get 'referrer' role here — they get it only after admin
      // approves their verification request.
      const profileUpdates: any = {
        location: [payload.city, payload.state, payload.country].filter(Boolean).join(', '),
        company_name: payload.company_name || null,
        mobile_number: payload.contact_number || null,
        linkedin_url: payload.linkedin_profile_link || null,
      };

      // `office_email` carries a verification flag (`office_email_verified`, set by
      // the verify-work-email-otp edge function). Only write it here when the value
      // matches the address that was actually proven, otherwise we'd leave a
      // verified flag attached to an unverified address.
      const proposedOfficeEmail = (payload.official_email || payload.company_email || '').trim().toLowerCase();
      if (proposedOfficeEmail) {
        const { data: proof } = await supabase
          .from('verified_work_emails')
          .select('email')
          .eq('user_id', session.user.id)
          .maybeSingle();
        const provenEmail = ((proof as any)?.email || '').toLowerCase();

        if (provenEmail && provenEmail === proposedOfficeEmail) {
          profileUpdates.office_email = proposedOfficeEmail;
        } else if (!provenEmail) {
          // Nothing proven yet (e.g. job seeker) — store it, but unverified.
          profileUpdates.office_email = proposedOfficeEmail;
          profileUpdates.office_email_verified = false;
          profileUpdates.office_email_verified_at = null;
        }
        // If a different address is already proven, leave profiles.office_email
        // alone; verify-work-email-otp owns that column.
      }

      if (payload.consented_at) {
        profileUpdates.consented_at = payload.consented_at;
        profileUpdates.consent_version = payload.consent_version;
        profileUpdates.is_adult = payload.is_adult;
      }

      if (isWP) {
        profileUpdates.role = 'referrer';
        profileUpdates.user_type = 'working_professional';
        profileUpdates.is_verified = true;

        // Resume Review is always on for working professionals — ensure a
        // default active config exists right from onboarding. Best-effort:
        // failure here doesn't block onboarding (the ServiceSetupGate and
        // migration backfill cover any gaps).
        try {
          await supabase.from('service_configurations').upsert(
            {
              user_id: session.user.id,
              service_id: 'resume-review',
              availability: {},
              price: 299,
              is_active: true,
              updated_at: new Date().toISOString(),
            } as any,
            { onConflict: 'user_id,service_id' }
          );
        } catch (svcErr) {
          console.warn('saveOnboarding: default resume-review config skipped', svcErr);
        }
      } else {
        profileUpdates.role = 'job_seeker';
        profileUpdates.user_type = 'student';
        profileUpdates.is_verified = true;
      }

      // Stamp a user_code if the profile doesn't already have one.
      // REEFJS for job seekers, REFFR for direct WP signups.
      // This is best-effort — failure doesn't block onboarding.
      try {
        const { data: existingProfile } = await supabase
          .from('profiles')
          .select('user_code')
          .eq('id', session.user.id)
          .single();

        if (!existingProfile?.user_code) {
          const prefix = isWP ? 'REFFR' : 'REEFJS';
          const { data: codeData } = await supabase
            .rpc('generate_user_code', { p_prefix: prefix });
          if (codeData) {
            profileUpdates.user_code = codeData;
          }
        }
      } catch (codeErr) {
        console.warn('saveOnboarding: user_code generation skipped', codeErr);
      }

      const { error: profileError } = await supabase
        .from('profiles')
        .update(profileUpdates)
        .eq('id', session.user.id);
      if (profileError && process.env.NODE_ENV === 'development') {
        console.warn('saveOnboarding: profiles update skipped or failed', profileError);
      }

      invalidateCachePrefix('user-profile:');
      invalidateCachePrefix('onboarding:');
      invalidateCachePrefix('referrer-dashboard:');
      invalidateCachePrefix('jobseeker-dashboard:');

      // A working professional just told us their company — kick off ingestion of
      // that company's career-page openings so they start appearing in Job
      // Openings. Fire-and-forget: never block or fail onboarding on this.
      if (isWP && (payload.company_name || '').trim()) {
        try {
          void supabase.functions.invoke('ingest-company-jobs', {
            body: { company: (payload.company_name || '').trim() },
          });
        } catch (ingestErr) {
          console.warn('saveOnboarding: job ingestion trigger skipped', ingestErr);
        }
      }

      return data && data.length > 0 ? data[0] : null;
    });
  }

  async getOnboarding(): Promise<ApiResponse<any>> {
    const session = await getSession();
    if (!session?.user) {
      return { success: false, error: 'Not authenticated', status: 401 };
    }
    return wrapAsync(
      async () => {
        const { data, error } = await supabase
          .from('user_onboarding')
          .select('*')
          .eq('user_id', session.user.id);
        if (error) throw error;
        return data && data.length > 0 ? data[0] : null;
      },
      { key: buildCacheKey(['onboarding', session.user.id]) }
    );
  }

  async updateOnboarding(payload: any): Promise<ApiResponse<any>> {
    const session = await getSession();
    if (!session?.user) {
      return { success: false, error: 'Not authenticated', status: 401 };
    }
    return wrapAsync(async () => {
      const { data, error } = await supabase
        .from('user_onboarding')
        .update(payload)
        .eq('user_id', session.user.id)
        .select();
      if (error) throw error;
      return data && data.length > 0 ? data[0] : null;
    });
  }

  /** Returns the WP verification status for the current user.
   *  Reads verification_status + admin_notes from user_onboarding. */
  async getVerificationStatus(): Promise<ApiResponse<{
    verification_status: 'pending' | 'approved' | 'rejected' | null;
    admin_notes: string | null;
    user_type: string | null;
  }>> {
    return wrapAsync(async () => {
      const session = await getSession();
      if (!session?.user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('user_onboarding')
        .select('verification_status, admin_notes, user_type')
        .eq('user_id', session.user.id);

      if (error) throw error;
      const record = data && data.length > 0 ? data[0] : null;
      return {
        verification_status: (record?.verification_status as 'pending' | 'approved' | 'rejected' | null) ?? null,
        admin_notes: record?.admin_notes ?? null,
        user_type: record?.user_type ?? null,
      };
    });
  }

  // ── Referrals ───────────────────────────────────────────

  async getReferrers(): Promise<ApiResponse<Profile[]>> {
    return wrapAsync(
      async () => {
        // Multi-signal rank scores (migration 026): Bayesian rating + response
        // rate + completion rate + recency + conversion. Falls back to the
        // plain ratings RPC if the new function isn't deployed yet.
        const [profilesRes, rankRes] = await Promise.all([
          supabase
            .from('profiles')
            .select(PUBLIC_PROFILE_SELECT)
            .eq('role', 'referrer')
            .eq('is_active', true)
            .order('created_at', { ascending: false }),
          supabase.rpc('get_referrer_rank_scores'),
        ]);

        if (profilesRes.error) throw profilesRes.error;

        let scoreRows: any[] | null = rankRes.error ? null : (rankRes.data as any[] | null);
        if (!scoreRows) {
          const ratingsRes = await supabase.rpc('get_referrer_ratings');
          scoreRows = (ratingsRes.data as any[] | null) || [];
        }

        const toNum = (v: unknown): number =>
          typeof v === 'string' ? parseFloat(v) : Number(v || 0);

        const ratingsMap = new Map<
          string,
          { average_rating: number | null; reviews_count: number; rank_score: number | null }
        >();
        for (const row of scoreRows) {
          if (!row?.referrer_id) continue;
          ratingsMap.set(row.referrer_id, {
            average_rating: row.average_rating == null ? null : toNum(row.average_rating),
            reviews_count: Number(row.reviews_count || 0),
            rank_score: row.rank_score == null ? null : toNum(row.rank_score),
          });
        }

        const profiles = (profilesRes.data || []) as unknown as Profile[];
        return profiles.map((profile) => {
          const info = ratingsMap.get(profile.id);
          return {
            ...profile,
            full_name: cleanExtractedName(profile.full_name) || profile.full_name,
            designation: cleanExtractedText(profile.designation) || profile.designation,
            company_name: cleanExtractedText(profile.company_name) || profile.company_name,
            rating: info ? info.average_rating : null,
            reviews_count: info ? info.reviews_count : 0,
            rank_score: info ? info.rank_score : null,
          };
        });
      },
      { key: 'referrers' }
    );
  }

  /**
   * Fetch reviewer avatars for a referrer's public profile (distinct people who
   * left ratings). Backed by the get_referrer_reviewers SECURITY DEFINER RPC so
   * it works past the RLS that restricts rating/PII reads to participants.
   */
  async getReferrerReviewers(
    referrerId: string,
    limit: number = 4
  ): Promise<ApiResponse<{
    reviewers: {
      id: string;
      avatarUrl: string | null;
      name: string;
      rating: number | null;
      reviewText: string | null;
      reviewedAt: string | null;
    }[];
    total: number;
  }>> {
    return wrapAsync(async () => {
      const { data, error } = await supabase.rpc('get_referrer_reviewers', {
        p_referrer_id: referrerId,
        p_limit: limit,
      });
      if (error) throw error;
      const rows = (data as any[] | null) || [];
      const reviewers = rows.map((r) => ({
        id: r.reviewer_id as string,
        avatarUrl: (r.avatar_url as string | null) || null,
        name: (r.display_name as string) || '',
        rating: r.rating != null ? Number(r.rating) : null,
        reviewText: (r.review_text as string | null) || null,
        reviewedAt: (r.reviewed_at as string | null) || null,
      }));
      const total = rows.length > 0 ? Number(rows[0].total_reviewers || rows.length) : 0;
      return { reviewers, total };
    });
  }

  /**
   * Admin-only: fetch complete user data by Unique User ID (user_code).
   * Returns profile, all request history (referrals, resume reviews, mock interviews),
   * referral sessions, referrer upgrade request, and success story.
   */
  async getAdminUserDetailByCode(userCode: string): Promise<ApiResponse<{
    profile: {
      id: string;
      full_name: string;
      primary_email: string;
      mobile_number: string | null;
      designation: string | null;
      company_name: string | null;
      location: string | null;
      role: string;
      is_verified: boolean;
      profile_picture: string | null;
      bio: string | null;
      skills: string[] | null;
      user_code: string;
      created_at: string;
      rating: number | null;
      reviews_count: number | null;
    } | null;
    referral_requests: any[];
    resume_review_requests: any[];
    mock_interview_requests: any[];
    referral_sessions: any[];
    upgrade_request: any | null;
    success_story: any | null;
  } | null>> {
    return wrapAsync(async () => {
      const session = await getSession();
      if (!session?.user) throw new Error('Not authenticated');
      const adminProfile = await getProfile(session.user.id);
      if (adminProfile?.role !== 'admin') throw new Error('Admin access required');

      // 1. Fetch profile by user_code
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('id, full_name, primary_email, mobile_number, designation, company_name, location, role, is_verified, profile_picture, bio, skills, user_code, created_at, rating, reviews_count')
        .eq('user_code', userCode.toUpperCase().trim())
        .maybeSingle();

      if (profileError) throw profileError;
      if (!profile) return null;

      const uid = profile.id;

      // 2. Fetch all user activity in parallel
      const [
        referralRes,
        resumeRes,
        mockRes,
        sessionsRes,
        upgradeRes,
        storyRes,
      ] = await Promise.allSettled([
        supabase.from('referral_requests').select('*').eq('requester_id', uid).order('created_at', { ascending: false }),
        supabase.from('resume_review_requests').select('*').eq('requester_id', uid).order('created_at', { ascending: false }),
        supabase.from('mock_interview_requests').select('*').eq('requester_id', uid).order('created_at', { ascending: false }),
        supabase.from('referral_sessions').select('*').or(`requester_id.eq.${uid},referrer_id.eq.${uid}`).order('scheduled_start', { ascending: false }),
        supabase.from('referrer_upgrade_requests').select('*').eq('user_id', uid).order('created_at', { ascending: false }).limit(1).maybeSingle(),
        supabase.from('success_stories').select('*').eq('user_id', uid).order('created_at', { ascending: false }).limit(1).maybeSingle(),
      ]);

      return {
        profile,
        referral_requests: referralRes.status === 'fulfilled' ? (referralRes.value.data || []) : [],
        resume_review_requests: resumeRes.status === 'fulfilled' ? (resumeRes.value.data || []) : [],
        mock_interview_requests: mockRes.status === 'fulfilled' ? (mockRes.value.data || []) : [],
        referral_sessions: sessionsRes.status === 'fulfilled' ? (sessionsRes.value.data || []) : [],
        upgrade_request: upgradeRes.status === 'fulfilled' ? (upgradeRes.value.data ?? null) : null,
        success_story: storyRes.status === 'fulfilled' ? (storyRes.value.data ?? null) : null,
      };
    });
  }

  /**
   * Looks up a profile by its unique user_code (e.g. REEFJS10042).
   * Returns a Professional-shaped object or null if not found.
   * This powers the "search by Unique User ID" feature in the global search modal.
   */
  async getUserByUserCode(code: string): Promise<ApiResponse<{
    id: string;
    full_name: string;
    designation: string | null;
    company_name: string | null;
    profile_picture: string | null;
    user_code: string;
    role: string;
    is_verified: boolean;
    bio: string | null;
    skills: string[] | null;
    rating: number | null;
    reviews_count: number | null;
  } | null>> {
    return wrapAsync(async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name, designation, company_name, profile_picture, user_code, role, is_verified, bio, skills, rating, reviews_count')
        .eq('user_code', code.toUpperCase().trim())
        .maybeSingle();

      if (error) throw error;
      return data ?? null;
    });
  }

  async createReferralRequest(payload: ReferralRequestPayload): Promise<ApiResponse<any>> {
    return wrapAsync(async () => {
      const session = await getSession();
      if (!session?.user) throw new Error('Not authenticated');

      const profile = await getProfile(session.user.id);
      const requesterName = profile?.full_name || 'Unknown User';

      let filePath: string | null = null;
      if (payload.resume_file) {
        const safeName = sanitizeFileName(payload.resume_file.name || 'resume.pdf');
        filePath = `${session.user.id}/referrals/${Date.now()}-${safeName}`;

        const { error: uploadError } = await supabase
          .storage
          .from('resume-reviews')
          .upload(filePath, payload.resume_file, {
            upsert: false,
            contentType: payload.resume_file.type || 'application/pdf',
          });
        if (uploadError) throw uploadError;
      }

      let resolvedReferrerId = payload.referrer_id || null;
      if (!resolvedReferrerId && payload.referrer_name) {
        try {
          const { data: refProfile } = await supabase
            .from('profiles')
            .select('id')
            .eq('full_name', payload.referrer_name)
            .eq('role', 'referrer')
            .maybeSingle();
          if (refProfile?.id) {
            resolvedReferrerId = refProfile.id;
          }
        } catch {
          // Ignore lookup fallback errors
        }
      }

      if (resolvedReferrerId && resolvedReferrerId === session.user.id) {
        throw new Error('You cannot request a referral from yourself.');
      }

      const { data, error } = await supabase
        .from('referral_requests')
        .insert({
          requester_id: session.user.id,
          requester_name: requesterName,
          referrer_id: resolvedReferrerId,
          referrer_name: payload.referrer_name,
          referrer_company: payload.referrer_company,
          referrer_title: payload.referrer_title || null,

          job_url: payload.job_url || null,
          job_title: payload.job_title || null,
          role_type: payload.role_type || null,
          message_template: payload.message_template || null,
          personal_message: payload.personal_message,
          technical_skills: payload.technical_skills || null,
          experience_level: payload.experience_level || null,
          priority: payload.priority || 'medium',
          resume_file_name: payload.resume_file_name || (payload.resume_file ? payload.resume_file.name : null),
          resume_file_path: filePath || payload.resume_file_path || null,
          preferred_day: payload.preferred_day || null,
          preferred_time: payload.preferred_time || null,
          preferred_timezone: payload.preferred_timezone || null,
          requested_duration_minutes: payload.requested_duration_minutes || null,
          status: 'pending',
          payment_order_id: payload.payment_order_id || null,
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    });
  }

  async getSignedResumeUrl(filePath: string): Promise<ApiResponse<{ signedUrl: string }>> {
    return wrapAsync(async () => {
      const url = await getSafeSignedResumeUrl('resume-reviews', filePath);
      if (!url) throw new Error('Could not generate signed resume URL');
      return { signedUrl: url };
    });
  }

  async uploadResumeFile(file: File): Promise<ApiResponse<{ filePath: string }>> {
    return wrapAsync(async () => {
      const session = await getSession();
      if (!session?.user) throw new Error('Not authenticated');

      const safeName = sanitizeFileName(file.name || 'resume.pdf');
      const filePath = `${session.user.id}/${Date.now()}-${safeName}`;

      const { error: uploadError } = await supabase
        .storage
        .from('resume-reviews')
        .upload(filePath, file, {
          upsert: false,
          contentType: file.type || 'application/pdf',
        });
      if (uploadError) throw uploadError;
      return { filePath };
    });
  }

  /**
   * Parse a resume PDF via AI to extract structured profile data.
   * Calls the parse-resume Edge Function which uses OpenAI.
   */
  async parseResume(
    filePath: string,
    userType: 'job_seeker' | 'working_professional' = 'job_seeker',
    rawText?: string
  ): Promise<ApiResponse<{ data: any; fieldsExtracted: number }>> {
    return wrapAsync(async () => {
      // parse-resume now validates the caller server-side (it invokes OpenAI on
      // our key, so anon callers are rejected). Fail fast here with a clear
      // message instead of firing a request that will 401.
      const session = await getSession();
      if (!session?.user) throw new Error('Not authenticated');
      const { data, error } = await supabase.functions.invoke('parse-resume', {
        body: { filePath, userType, rawText },
      });
      if (error) {
        let msg = error.message;
        if ('context' in error && error.context) {
          try {
            const body = await (error.context as Response).json();
            if (body?.error) msg = body.error;
          } catch {
            /* ignore json parse error */
          }
        }
        throw new Error(msg || 'Failed to parse resume');
      }
      if (!data?.success) {
        throw new Error(data?.error || 'Failed to parse resume');
      }
      return { data: data.data, fieldsExtracted: data.fieldsExtracted || 0 };
    });
  }

  /**
   * Fetch the active list price a referrer charges for a given service.
   * Used to snapshot gross_amount onto a transaction row at creation time
   * so platform fee / net payout don't drift if the price is later edited.
   * Returns null when no active config exists.
   */
  private async fetchServicePriceForReferrer(
    referrerId: string,
    serviceId: 'referral-session' | 'interview-mock' | 'resume-review',
  ): Promise<number | null> {
    try {
      const { data, error } = await supabase
        .from('service_configurations')
        .select('price')
        .eq('user_id', referrerId)
        .eq('service_id', serviceId)
        .eq('is_active', true)
        .maybeSingle();
      if (error || !data) return null;
      return typeof data.price === 'number' ? data.price : null;
    } catch {
      return null;
    }
  }

  /**
   * Advances every past session to `completed` through the scheduled server sweep.
   *
   * This used to read and update session rows belonging to other users, which only
   * worked because of the broad authenticated policies removed in migration 037.
   * `auto_complete_past_sessions()` is the same routine pg_cron already runs, so
   * invoking it here is a nudge rather than a second implementation.
   */
  private async runSessionCompletionSweep(): Promise<void> {
    try {
      const { error } = await supabase.rpc('auto_complete_past_sessions');
      if (error) {
        console.warn('Session completion sweep did not run:', error.message);
      }
    } catch (err) {
      console.warn('Error running session completion sweep:', err);
    }
  }

  private async autoCompletePastReferralSessions(): Promise<void> {
    await this.runSessionCompletionSweep();
  }

  private async autoCompletePastMockInterviewSessions(): Promise<void> {
    await this.runSessionCompletionSweep();
  }

  async getReferralRequests(): Promise<ApiResponse<any[]>> {
    return wrapAsync(async () => {
      const devBypass = isDevAuthBypassEnabled();
      if (devBypass) {
        const devId = localStorage.getItem('DEV_USER_ID') || 'dev-user';
        const { data, error } = await supabase
          .from('referral_requests')
          .select('*')
          .eq('requester_id', devId)
          .order('created_at', { ascending: false });
        if (error) throw error;
        return data || [];
      }

      const session = await getSession();
      if (!session?.user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('referral_requests')
        .select('*')
        .eq('requester_id', session.user.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data || [];
    });
  }

  async getReferralRequestById(requestId: number): Promise<ApiResponse<any>> {
    return wrapAsync(async () => {
      const session = await getSession();
      if (!session?.user) throw new Error('Not authenticated');

      const { data: request, error } = await supabase
        .from('referral_requests')
        .select('*')
        .eq('id', requestId)
        .single();
      if (error) throw error;

      let requesterProfile: Profile | null = null;
      if (request?.requester_id) {
        const { data: profileRow, error: profileError } = await supabase
          .from('profiles')
          .select(SERVICE_PARTICIPANT_PROFILE_SELECT)
          .eq('id', request.requester_id)
          .maybeSingle();
        if (profileError) {
          console.warn('Referral request requester profile lookup failed', profileError);
        }
        requesterProfile = (profileRow as unknown as Profile) || null;
      }

      return {
        request,
        requester_profile: requesterProfile,
      };
    });
  }

  async getReferralRequestsForReferrer(): Promise<ApiResponse<any[]>> {
    return wrapAsync(async () => {
      const devBypass = isDevAuthBypassEnabled();
      if (devBypass) {
        const devId = localStorage.getItem('DEV_USER_ID') || 'dev-referrer';
        const { data, error } = await supabase
          .from('referral_requests')
          .select('*')
          .or(`referrer_id.eq.${devId},referrer_name.eq.${localStorage.getItem('DEV_USER_NAME') || ''}`)
          .order('created_at', { ascending: false });
        if (error) throw error;
        return data || [];
      }

      const session = await getSession();
      if (!session?.user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('referral_requests')
        .select('*')
        .eq('referrer_id', session.user.id)
        .order('created_at', { ascending: false });
      if (error) throw error;

      const requesterIds = Array.from(new Set((data || []).map((req: any) => req.requester_id).filter(Boolean)));
      let requesterProfileMap = new Map<string, Profile>();
      if (requesterIds.length > 0) {
        const { data: profiles, error: profileError } = await supabase
          .from('profiles')
          .select(SERVICE_PARTICIPANT_PROFILE_SELECT)
          .in('id', requesterIds);
        if (profileError) {
          console.warn('Referral requests requester profile lookup failed', profileError);
        }
        (profiles || []).forEach((p: any) => requesterProfileMap.set(p.id, p as Profile));
      }

      const results = (data || []).map((req: any) => ({
        ...req,
        requester_profile: requesterProfileMap.get(req.requester_id) || null,
      }));

      return results;
    });
  }

  async declineReferralRequest(requestId: string | number): Promise<ApiResponse<any>> {
    return wrapAsync(async () => {
      const { data, error } = await supabase
        .from('referral_requests')
        .update({ status: 'declined' })
        .eq('id', Number(requestId))
        .select()
        .single();
      if (error) throw error;
      return data;
    });
  }

  private async autoCancelExpiredResumeReviews(): Promise<void> {
    try {
      const expirationDate = new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString();
      const { error } = await supabase
        .from('resume_review_requests')
        .update({ status: 'cancelled' } as any)
        .eq('status', 'pending')
        .lt('created_at', expirationDate);
      if (error) {
        console.error('Error auto-cancelling expired resume reviews:', error);
      }
    } catch (err) {
      console.error('Failed to auto-cancel expired resume reviews:', err);
    }
  }

  async createResumeReviewRequest(payload: ResumeReviewRequestPayload): Promise<ApiResponse<ResumeReviewRequestRecord>> {
    return wrapAsync(async () => {
      const session = await getSession();
      if (!session?.user) throw new Error('Not authenticated');

      if (payload.referrer_id && payload.referrer_id === session.user.id) {
        throw new Error('You cannot book a resume review with yourself.');
      }

      const profile = await getProfile(session.user.id);
      const requesterName = profile?.full_name || 'Unknown User';

      const safeName = sanitizeFileName(payload.resume_file.name || 'resume.pdf');
      const filePath = `${session.user.id}/${Date.now()}-${safeName}`;

      const { error: uploadError } = await supabase
        .storage
        .from('resume-reviews')
        .upload(filePath, payload.resume_file, {
          upsert: false,
          contentType: payload.resume_file.type || 'application/pdf',
        });
      if (uploadError) throw uploadError;

      // Snapshot gross at request time. Resume review is platform-fixed at
      // ₹299 today; if a referrer ever overrides via service_configurations,
      // honor that. Locking the value here means the payout never shifts
      // even if the price changes later.
      const configured = payload.referrer_id
        ? await this.fetchServicePriceForReferrer(payload.referrer_id, 'resume-review')
        : null;
      const grossAmount = configured ?? PLATFORM_FIXED_SERVICE_PRICES['resume-review'];

      const { data, error } = await supabase
        .from('resume_review_requests')
        .insert({
          requester_id: session.user.id,
          requester_name: requesterName,
          referrer_id: payload.referrer_id || null,
          referrer_name: payload.referrer_name,
          referrer_company: payload.referrer_company,
          referrer_title: payload.referrer_title || null,
          focus_note: payload.focus_note || null,
          jd_url: payload.jd_url || null,
          resume_file_path: filePath,
          status: 'pending',
          gross_amount: grossAmount,
          payment_order_id: payload.payment_order_id || null,
        })
        .select()
        .single();
      if (error) throw error;
      return data as ResumeReviewRequestRecord;
    });
  }

  async getResumeReviewRequestsForRequester(): Promise<ApiResponse<ResumeReviewRequestSummary[]>> {
    return wrapAsync(async () => {
      await this.autoCancelExpiredResumeReviews();
      const session = await getSession();
      if (!session?.user) throw new Error('Not authenticated');

      const { data: requests, error } = await supabase
        .from('resume_review_requests')
        .select('*')
        .eq('requester_id', session.user.id)
        .order('created_at', { ascending: false });
      if (error) throw error;

      const profile = await getProfile(session.user.id);

      const referrerIds = Array.from(new Set((requests || []).map((req: any) => req.referrer_id).filter(Boolean)));
      let referrerProfileMap = new Map<string, Profile>();
      if (referrerIds.length > 0) {
        const { data: profiles, error: profileError } = await supabase
          .from('profiles')
          .select(SERVICE_PARTICIPANT_PROFILE_SELECT)
          .in('id', referrerIds);
        if (profileError) {
          console.warn('Resume review referrer profile lookup failed', profileError);
        }
        (profiles || []).forEach((p: any) => referrerProfileMap.set(p.id, p as Profile));
      }

      const summaries = await Promise.all((requests || []).map(async (request: any) => {
        const resumeUrl = await getSafeSignedResumeUrl('resume-reviews', request.resume_file_path);
        return {
          request: request as ResumeReviewRequestRecord,
          resume_url: resumeUrl,
          requester_profile: (profile as Profile) || null,
          referrer_profile: request.referrer_id ? (referrerProfileMap.get(request.referrer_id) || null) : null,
        };
      }));

      return summaries;
    });
  }

  async getResumeReviewRequestsForReferrer(): Promise<ApiResponse<ResumeReviewRequestSummary[]>> {
    return wrapAsync(async () => {
      await this.autoCancelExpiredResumeReviews();
      const session = await getSession();
      if (!session?.user) throw new Error('Not authenticated');

      const { data: requests, error } = await supabase
        .from('resume_review_requests')
        .select('*')
        .eq('referrer_id', session.user.id)
        .order('created_at', { ascending: false });
      if (error) throw error;

      const requesterIds = Array.from(new Set((requests || []).map((req: any) => req.requester_id)));
      let profileMap = new Map<string, Profile>();
      if (requesterIds.length > 0) {
        const { data: profiles, error: profileError } = await supabase
          .from('profiles')
          .select(SERVICE_PARTICIPANT_PROFILE_SELECT)
          .in('id', requesterIds);
        if (profileError) {
          console.warn('Resume review profile lookup failed', profileError);
        }
        (profiles || []).forEach((p: any) => profileMap.set(p.id, p as Profile));
      }

      const summaries = await Promise.all((requests || []).map(async (request: any) => {
        const resumeUrl = await getSafeSignedResumeUrl('resume-reviews', request.resume_file_path);
        return {
          request: request as ResumeReviewRequestRecord,
          resume_url: resumeUrl,
          requester_profile: profileMap.get(request.requester_id) || null,
        };
      }));

      return summaries;
    });
  }

  async declineResumeReviewRequest(requestId: string | number): Promise<ApiResponse<any>> {
    return wrapAsync(async () => {
      // The resume_review_requests CHECK constraint only permits
      // 'pending | feedback_delivered | cancelled' — 'declined' is not valid.
      // We write 'cancelled' to match what the constraint allows.
      const { data, error } = await supabase
        .from('resume_review_requests')
        .update({ status: 'cancelled' })
        .eq('id', Number(requestId))
        .select()
        .single();
      if (error) throw error;
      return data;
    });
  }

  async getResumeReviewRequestById(requestId: number): Promise<ApiResponse<ResumeReviewRequestSummary>> {
    return wrapAsync(async () => {
      await this.autoCancelExpiredResumeReviews();
      const session = await getSession();
      if (!session?.user) throw new Error('Not authenticated');

      const { data: request, error } = await supabase
        .from('resume_review_requests')
        .select('*, resume_review_ratings(*)')
        .eq('id', requestId)
        .single();
      if (error) throw error;

      let requesterProfile: Profile | null = null;
      if (request?.requester_id) {
        const { data: profileRow, error: profileError } = await supabase
          .from('profiles')
          .select(SERVICE_PARTICIPANT_PROFILE_SELECT)
          .eq('id', request.requester_id)
          .maybeSingle();
        if (profileError) {
          console.warn('Resume review requester profile lookup failed', profileError);
        }
        requesterProfile = (profileRow as unknown as Profile) || null;
      }

      let resumeUrl: string | null = null;
      if (request?.resume_file_path) {
        resumeUrl = await getSafeSignedResumeUrl('resume-reviews', request.resume_file_path);
      }

      return {
        request: request as ResumeReviewRequestRecord,
        resume_url: resumeUrl,
        requester_profile: requesterProfile,
      };
    });
  }

  async submitResumeReviewFeedback(requestId: number, payload: { feedback_text: string }): Promise<ApiResponse<ResumeReviewRequestRecord>> {
    return wrapAsync(async () => {
      const session = await getSession();
      if (!session?.user) throw new Error('Not authenticated');

      const now = new Date().toISOString();
      const { data, error } = await supabase
        .from('resume_review_requests')
        .update({
          feedback_text: payload.feedback_text,
          feedback_submitted_at: now,
          status: 'feedback_delivered',
          updated_at: now,
        })
        .eq('id', requestId)
        .select()
        .single();
      if (error) throw error;
      return data as ResumeReviewRequestRecord;
    });
  }

  async getReferralSessionsForUser(): Promise<ApiResponse<ReferralSessionRecord[]>> {
    await this.autoCompletePastReferralSessions();
    return wrapAsync(async () => {
      const devBypass = isDevAuthBypassEnabled();
      let sessionsData: any[] = [];
      if (devBypass) {
        const { data, error } = await supabase
          .from('referral_sessions')
          .select('*, referral_requests(resume_file_name, resume_file_path)')
          .order('scheduled_start', { ascending: true });
        if (error) throw error;
        sessionsData = data || [];
      } else {
        const session = await getSession();
        if (!session?.user) throw new Error('Not authenticated');

        const { data, error } = await supabase
          .from('referral_sessions')
          .select('*, referral_requests(resume_file_name, resume_file_path)')
          .or(`requester_id.eq.${session.user.id},referrer_id.eq.${session.user.id}`)
          .order('scheduled_start', { ascending: true });
        if (error) throw error;
        sessionsData = data || [];
      }

      const userIds = new Set<string>();
      sessionsData.forEach((s) => {
        if (s.requester_id) userIds.add(s.requester_id);
        if (s.referrer_id) userIds.add(s.referrer_id);
      });

      let profileMap = new Map<string, Profile>();
      if (userIds.size > 0) {
        const { data: profiles, error: profileError } = await supabase
          .from('profiles')
          .select(SERVICE_PARTICIPANT_PROFILE_SELECT)
          .in('id', Array.from(userIds));
        if (profileError) {
          console.warn('Referral sessions profile lookup failed', profileError);
        }
        (profiles || []).forEach((p: any) => profileMap.set(p.id, p as Profile));
      }

      const results = sessionsData.map((s) => ({
        ...s,
        requester_profile: profileMap.get(s.requester_id) || null,
        referrer_profile: profileMap.get(s.referrer_id) || null,
      }));

      return results as ReferralSessionRecord[];
    });
  }

  async getReferralSessionsForReferrer(referrerId: string): Promise<ApiResponse<ReferralSessionRecord[]>> {
    await this.autoCompletePastReferralSessions();
    return wrapAsync(async () => {
      // Availability reads go through a minimized server projection. Session rows
      // are no longer readable across users, so nothing here can leak a room URL,
      // a participant name, or a price.
      const { data, error } = await supabase.rpc('get_referrer_busy_slots', {
        p_referrer_id: referrerId,
      });
      if (error) throw error;
      return ((data || []) as BusySlot[])
        .filter((slot) => slot.service_id === 'referral-session')
        .map((slot) => ({
          scheduled_start: slot.scheduled_start,
          scheduled_end: slot.scheduled_end,
          duration_minutes: slot.duration_minutes,
          status: slot.status,
        })) as unknown as ReferralSessionRecord[];
    });
  }

  async getReferralSessionById(sessionId: number): Promise<ApiResponse<ReferralSessionRecord>> {
    await this.autoCompletePastReferralSessions();
    return wrapAsync(async () => {
      const devBypass = isDevAuthBypassEnabled();
      if (devBypass) {
        const { data, error } = await supabase
          .from('referral_sessions')
          .select('*, referral_requests(resume_file_name, resume_file_path), referral_session_ratings(*)')
          .eq('id', sessionId)
          .single();
        if (error) throw error;
        return data as ReferralSessionRecord;
      }

      const session = await getSession();
      if (!session?.user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('referral_sessions')
        .select('*, referral_requests(resume_file_name, resume_file_path), referral_session_ratings(*)')
        .eq('id', sessionId)
        .single();
      if (error) throw error;
      return data as ReferralSessionRecord;
    });
  }

  async scheduleReferralSession(payload: ReferralSessionSchedulePayload): Promise<ApiResponse<ReferralSessionRecord>> {
    return wrapAsync(async () => {
      const devBypass = isDevAuthBypassEnabled();
      let referrerId = payload.referrer_id;
      let referrerName = payload.referrer_name;
      let referrerCompany = payload.referrer_company;

      if (devBypass) {
        referrerId = localStorage.getItem('DEV_USER_ID') || referrerId;
        referrerName = localStorage.getItem('DEV_USER_NAME') || referrerName;
      } else {
        const session = await getSession();
        if (!session?.user) throw new Error('Not authenticated');
        const profile = await getProfile(session.user.id);
        referrerName = referrerName || profile?.full_name || null;
        referrerCompany = referrerCompany || profile?.company_name || null;
      }

      const grossAmount = referrerId
        ? await this.fetchServicePriceForReferrer(referrerId, 'referral-session')
        : null;

      const { data, error } = await supabase
        .from('referral_sessions')
        .insert({
          request_id: payload.request_id,
          requester_id: payload.requester_id,
          referrer_id: referrerId,
          referrer_name: referrerName || null,
          referrer_company: referrerCompany || null,
          scheduled_start: payload.scheduled_start,
          scheduled_end: payload.scheduled_end,
          duration_minutes: payload.duration_minutes,
          status: 'scheduled',
          gross_amount: grossAmount,
        })
        .select()
        .single();
      if (error) throw error;

      if (payload.request_id) {
        await supabase
          .from('referral_requests')
          .update({ status: 'scheduled', referrer_id: payload.referrer_id })
          .eq('id', payload.request_id);
      }

      return data as ReferralSessionRecord;
    });
  }

  async rescheduleReferralSession(sessionId: number, updates: {
    scheduled_start: string;
    scheduled_end: string;
    duration_minutes: number;
  }): Promise<ApiResponse<ReferralSessionRecord>> {
    return wrapAsync(async () => {
      const devBypass = isDevAuthBypassEnabled();
      if (!devBypass) {
        const session = await getSession();
        if (!session?.user) throw new Error('Not authenticated');
      }

      const { data: existing, error: fetchError } = await supabase
        .from('referral_sessions')
        .select('reschedule_count')
        .eq('id', sessionId)
        .single();
      if (fetchError) throw fetchError;

      const { data, error } = await supabase
        .from('referral_sessions')
        .update({
          scheduled_start: updates.scheduled_start,
          scheduled_end: updates.scheduled_end,
          duration_minutes: updates.duration_minutes,
          status: 'rescheduled',
          reschedule_count: (existing?.reschedule_count || 0) + 1,
          updated_at: new Date().toISOString(),
        })
        .eq('id', sessionId)
        .select()
        .single();
      if (error) throw error;

      if (data?.request_id) {
        await supabase
          .from('referral_requests')
          .update({ status: 'rescheduled' })
          .eq('id', data.request_id);
      }
      return data as ReferralSessionRecord;
    });
  }

  async cancelReferralSession(sessionId: number, reason?: string): Promise<ApiResponse<ReferralSessionRecord>> {
    return wrapAsync(async () => {
      const devBypass = isDevAuthBypassEnabled();
      let cancelledBy = null;
      if (devBypass) {
        cancelledBy = localStorage.getItem('DEV_USER_ID') || 'dev-user';
      } else {
        const session = await getSession();
        if (!session?.user) throw new Error('Not authenticated');
        cancelledBy = session.user.id;
      }

      const { data, error } = await supabase
        .from('referral_sessions')
        .update({
          status: 'cancelled',
          cancelled_at: new Date().toISOString(),
          cancelled_by: cancelledBy,
          cancel_reason: reason || null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', sessionId)
        .select()
        .single();
      if (error) throw error;

      if (data?.request_id) {
        await supabase
          .from('referral_requests')
          .update({ status: 'cancelled' })
          .eq('id', data.request_id);
      }
      return data as ReferralSessionRecord;
    });
  }

  // ── Mock Interviews (Culture Call) ───────────────────────────

  async createMockInterviewRequest(payload: MockInterviewRequestPayload): Promise<ApiResponse<any>> {
    return wrapAsync(async () => {
      const session = await getSession();
      if (!session?.user) throw new Error('Not authenticated');

      if (payload.referrer_id && payload.referrer_id === session.user.id) {
        throw new Error('You cannot book a mock interview with yourself.');
      }

      const profile = await getProfile(session.user.id);
      const requesterId = session.user.id;
      const requesterName = profile?.full_name || 'Unknown User';

      const { data, error } = await supabase
        .from('mock_interview_requests')
        .insert({
          requester_id: requesterId,
          requester_name: requesterName,
          referrer_id: payload.referrer_id || null,
          referrer_name: payload.referrer_name,
          referrer_company: payload.referrer_company,
          referrer_title: payload.referrer_title || null,
          company_question: payload.company_question || null,
          focus_note: payload.focus_note || null,
          preferred_day: payload.preferred_day || null,
          preferred_time: payload.preferred_time || null,
          preferred_timezone: payload.preferred_timezone || null,
          requested_duration_minutes: payload.requested_duration_minutes || null,
          price: payload.price || null,
          status: 'pending',
          payment_order_id: payload.payment_order_id || null,
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    });
  }

  async getMockInterviewRequests(): Promise<ApiResponse<any[]>> {
    return wrapAsync(async () => {
      const session = await getSession();
      if (!session?.user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('mock_interview_requests')
        .select('*')
        .eq('requester_id', session.user.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data || [];
    });
  }

  async getMockInterviewRequestsForReferrer(): Promise<ApiResponse<any[]>> {
    return wrapAsync(async () => {
      const session = await getSession();
      if (!session?.user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('mock_interview_requests')
        .select('*')
        .eq('referrer_id', session.user.id)
        .order('created_at', { ascending: false });
      if (error) throw error;

      const requesterIds = Array.from(new Set((data || []).map((req: any) => req.requester_id).filter(Boolean)));
      let requesterProfileMap = new Map<string, Profile>();
      if (requesterIds.length > 0) {
        const { data: profiles, error: profileError } = await supabase
          .from('profiles')
          .select(SERVICE_PARTICIPANT_PROFILE_SELECT)
          .in('id', requesterIds);
        if (profileError) {
          console.warn('Mock requests requester profile lookup failed', profileError);
        }
        (profiles || []).forEach((p: any) => requesterProfileMap.set(p.id, p as Profile));
      }

      const results = (data || []).map((req: any) => ({
        ...req,
        requester_profile: requesterProfileMap.get(req.requester_id) || null,
      }));

      return results;
    });
  }

  async declineMockInterviewRequest(requestId: string | number): Promise<ApiResponse<any>> {
    return wrapAsync(async () => {
      const { data, error } = await supabase
        .from('mock_interview_requests')
        .update({ status: 'declined' })
        .eq('id', Number(requestId))
        .select()
        .single();
      if (error) throw error;
      return data;
    });
  }

  async getMockInterviewRequestById(requestId: number): Promise<ApiResponse<any>> {
    return wrapAsync(async () => {
      const session = await getSession();
      if (!session?.user) throw new Error('Not authenticated');

      const { data: request, error } = await supabase
        .from('mock_interview_requests')
        .select('*')
        .eq('id', requestId)
        .single();
      if (error) throw error;

      let requesterProfile: Profile | null = null;
      if (request?.requester_id) {
        const { data: profileRow, error: profileError } = await supabase
          .from('profiles')
          .select(SERVICE_PARTICIPANT_PROFILE_SELECT)
          .eq('id', request.requester_id)
          .maybeSingle();
        if (profileError) {
          console.warn('Mock interview requester profile lookup failed', profileError);
        }
        requesterProfile = (profileRow as unknown as Profile) || null;
      }

      return {
        request,
        requester_profile: requesterProfile,
      };
    });
  }

  async getMockInterviewSessionsForUser(): Promise<ApiResponse<MockInterviewSessionRecord[]>> {
    await this.autoCompletePastMockInterviewSessions();
    return wrapAsync(async () => {
      const session = await getSession();
      if (!session?.user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('mock_interview_sessions')
        .select('*')
        .or(`requester_id.eq.${session.user.id},referrer_id.eq.${session.user.id}`)
        .order('scheduled_start', { ascending: true });
      if (error) throw error;
      const sessionsData = data || [];

      const userIds = new Set<string>();
      sessionsData.forEach((s) => {
        if (s.requester_id) userIds.add(s.requester_id);
        if (s.referrer_id) userIds.add(s.referrer_id);
      });

      let profileMap = new Map<string, Profile>();
      if (userIds.size > 0) {
        const { data: profiles, error: profileError } = await supabase
          .from('profiles')
          .select(SERVICE_PARTICIPANT_PROFILE_SELECT)
          .in('id', Array.from(userIds));
        if (profileError) {
          console.warn('Mock sessions profile lookup failed', profileError);
        }
        (profiles || []).forEach((p: any) => profileMap.set(p.id, p as Profile));
      }

      const results = sessionsData.map((s) => ({
        ...s,
        requester_profile: profileMap.get(s.requester_id) || null,
        referrer_profile: profileMap.get(s.referrer_id) || null,
      }));

      return results as MockInterviewSessionRecord[];
    });
  }

  async getMockInterviewSessionsForReferrer(referrerId: string): Promise<ApiResponse<MockInterviewSessionRecord[]>> {
    await this.autoCompletePastMockInterviewSessions();
    return wrapAsync(async () => {
      const { data, error } = await supabase.rpc('get_referrer_busy_slots', {
        p_referrer_id: referrerId,
      });
      if (error) throw error;
      return ((data || []) as BusySlot[])
        .filter((slot) => slot.service_id === 'culture-call')
        .map((slot) => ({
          scheduled_start: slot.scheduled_start,
          scheduled_end: slot.scheduled_end,
          duration_minutes: slot.duration_minutes,
          status: slot.status,
        })) as unknown as MockInterviewSessionRecord[];
    });
  }

  async getMockInterviewSessionById(sessionId: number): Promise<ApiResponse<MockInterviewSessionRecord>> {
    await this.autoCompletePastMockInterviewSessions();
    return wrapAsync(async () => {
      const session = await getSession();
      if (!session?.user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('mock_interview_sessions')
        .select('*, mock_interview_session_ratings(*)')
        .eq('id', sessionId)
        .single();
      if (error) throw error;
      return data as MockInterviewSessionRecord;
    });
  }

  async scheduleMockInterviewSession(payload: MockInterviewSessionSchedulePayload): Promise<ApiResponse<MockInterviewSessionRecord>> {
    return wrapAsync(async () => {
      const session = await getSession();
      if (!session?.user) throw new Error('Not authenticated');
      const profile = await getProfile(session.user.id);
      const referrerId = session.user.id;
      const referrerName = payload.referrer_name || profile?.full_name || null;
      const referrerCompany = payload.referrer_company || profile?.company_name || null;

      // Snapshot gross at booking: prefer the price stored on the request
      // (set when the seeker submitted it), fall back to the referrer's
      // live 'interview-mock' service price.
      let grossAmount: number | null = null;
      if (payload.request_id) {
        const { data: req } = await supabase
          .from('mock_interview_requests')
          .select('price')
          .eq('id', payload.request_id)
          .maybeSingle();
        if (req && typeof req.price === 'number') grossAmount = req.price;
      }
      if (grossAmount == null) {
        grossAmount = await this.fetchServicePriceForReferrer(referrerId, 'interview-mock');
      }

      const { data, error } = await supabase
        .from('mock_interview_sessions')
        .insert({
          request_id: payload.request_id,
          requester_id: payload.requester_id,
          referrer_id: referrerId,
          referrer_name: referrerName || null,
          referrer_company: referrerCompany || null,
          scheduled_start: payload.scheduled_start,
          scheduled_end: payload.scheduled_end,
          duration_minutes: payload.duration_minutes,
          status: 'scheduled',
          gross_amount: grossAmount,
        })
        .select()
        .single();
      if (error) throw error;

      if (payload.request_id) {
        await supabase
          .from('mock_interview_requests')
          .update({ status: 'scheduled', referrer_id: referrerId })
          .eq('id', payload.request_id);
      }

      return data as MockInterviewSessionRecord;
    });
  }

  async rescheduleMockInterviewSession(sessionId: number, updates: {
    scheduled_start: string;
    scheduled_end: string;
    duration_minutes: number;
  }): Promise<ApiResponse<MockInterviewSessionRecord>> {
    return wrapAsync(async () => {
      const session = await getSession();
      if (!session?.user) throw new Error('Not authenticated');

      const { data: existing, error: fetchError } = await supabase
        .from('mock_interview_sessions')
        .select('reschedule_count')
        .eq('id', sessionId)
        .single();
      if (fetchError) throw fetchError;

      const { data, error } = await supabase
        .from('mock_interview_sessions')
        .update({
          scheduled_start: updates.scheduled_start,
          scheduled_end: updates.scheduled_end,
          duration_minutes: updates.duration_minutes,
          status: 'rescheduled',
          reschedule_count: (existing?.reschedule_count || 0) + 1,
          updated_at: new Date().toISOString(),
        })
        .eq('id', sessionId)
        .select()
        .single();
      if (error) throw error;

      if (data?.request_id) {
        await supabase
          .from('mock_interview_requests')
          .update({ status: 'rescheduled' })
          .eq('id', data.request_id);
      }
      return data as MockInterviewSessionRecord;
    });
  }

  async cancelMockInterviewSession(sessionId: number, reason?: string): Promise<ApiResponse<MockInterviewSessionRecord>> {
    return wrapAsync(async () => {
      const session = await getSession();
      if (!session?.user) throw new Error('Not authenticated');
      const cancelledBy = session.user.id;

      const { data, error } = await supabase
        .from('mock_interview_sessions')
        .update({
          status: 'cancelled',
          cancelled_at: new Date().toISOString(),
          cancelled_by: cancelledBy,
          cancel_reason: reason || null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', sessionId)
        .select()
        .single();
      if (error) throw error;

      if (data?.request_id) {
        await supabase
          .from('mock_interview_requests')
          .update({ status: 'cancelled' })
          .eq('id', data.request_id);
      }
      return data as MockInterviewSessionRecord;
    });
  }

  async getDailyJoinToken(sessionId: number, sessionType: 'referral' | 'mock' = 'referral'): Promise<ApiResponse<{ roomUrl: string; token: string }>> {
    return wrapAsync(async () => {
      const { data, error } = await supabase.functions.invoke('daily-join', {
        body: { sessionId, sessionType },
      });
      if (error) throw error;
      if (!data?.roomUrl || !data?.token) throw new Error('Daily token response missing');
      return { roomUrl: data.roomUrl, token: data.token };
    });
  }

  // ── Referrer upgrade (I am hired) ─────────────────────────

  async createReferrerUpgradeRequest(payload: {
    company_name: string;
    company_email: string;
    referrer_code?: string | null;
  }): Promise<ApiResponse<any>> {
    return wrapAsync(async () => {
      const session = await getSession();
      if (!session?.user) throw new Error('Not authenticated');

      const profile = await getProfile(session.user.id);
      if (!profile) throw new Error('Profile not found');
      if (profile.role === 'referrer') {
        throw new Error('You already have a working professional dashboard.');
      }

      const { data: existing } = await supabase
        .from('referrer_upgrade_requests')
        .select('id, status')
        .eq('user_id', session.user.id)
        .eq('status', 'pending')
        .maybeSingle();

      if (existing) {
        throw new Error('You already have a pending upgrade request.');
      }

      const { data, error } = await supabase
        .from('referrer_upgrade_requests')
        .insert({
          user_id: session.user.id,
          user_name: profile.full_name,
          user_email: profile.primary_email,
          company_name: payload.company_name.trim(),
          company_email: payload.company_email.trim(),
          referrer_code: payload.referrer_code?.trim().toUpperCase() || null,
          status: 'pending',
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    });
  }

  /**
   * Self-service upgrade to working professional (referrer) after the caller's
   * company mail ID has been OTP-verified (verified_work_emails proof exists).
   *
   * Backed by the SECURITY DEFINER RPC `self_upgrade_to_referrer` (migration 063),
   * which refuses to upgrade unless the proof is present — so this cannot flip a
   * user's role without a real, verified company email. Returns the recorded
   * upgrade request id (for attaching a success story) and the new user_code.
   */
  async selfUpgradeToReferrer(payload: {
    company_name: string;
    referrer_code?: string | null;
  }): Promise<ApiResponse<{
    upgrade_request_id: number;
    user_code: string | null;
    company_email: string;
    /** Company actually stored on the profile after reconciliation with the verified domain. */
    company_name: string;
    /** Domain of the OTP-verified company email (the authoritative employer proof). */
    verified_domain: string | null;
    was_already_referrer: boolean;
  }>> {
    return wrapAsync(async () => {
      const session = await getSession();
      if (!session?.user) throw new Error('Not authenticated');

      const { data, error } = await supabase.rpc('self_upgrade_to_referrer', {
        p_company_name: payload.company_name.trim(),
        p_referrer_code: payload.referrer_code?.trim().toUpperCase() || null,
      });
      if (error) throw error;
      return data as {
        upgrade_request_id: number;
        user_code: string | null;
        company_email: string;
        company_name: string;
        verified_domain: string | null;
        was_already_referrer: boolean;
      };
    });
  }

  async createSuccessStory(payload: {
    upgrade_request_id: number;
    referrer_id: string | null;
    company_name: string;
    role_title: string;
    quote: string | null;
    auto_generated_quote: string;
  }): Promise<ApiResponse<SuccessStory>> {
    return wrapAsync(async () => {
      const session = await getSession();
      if (!session?.user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('success_stories')
        .insert({
          user_id: session.user.id,
          upgrade_request_id: payload.upgrade_request_id,
          referrer_id: payload.referrer_id,
          company_name: payload.company_name,
          role_title: payload.role_title,
          quote: payload.quote || null,
          auto_generated_quote: payload.auto_generated_quote,
          // Self-service upgrade flow has no admin moderation — stories publish immediately.
          is_approved: true,
        })
        .select()
        .single();

      if (error) {
        // 42P01 = table does not exist; degrade gracefully so modal still advances
        if (error.code === '42P01') {
          console.warn('Table "success_stories" does not exist. Run migration 035.');
        }
        throw error;
      }
      return data as SuccessStory;
    });
  }

  async getApprovedSuccessStories(): Promise<ApiResponse<SuccessStory[]>> {
    return wrapAsync(async () => {
      const { data, error } = await supabase
        .from('success_stories')
        .select('*, referrer:referrer_id(full_name)')
        .eq('is_approved', true)
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) {
        if (error.code === '42P01') {
          console.warn('Table "success_stories" does not exist.');
          return [];
        }
        throw error;
      }

      // Flatten joined referrer name
      const stories = (data || []).map((row: any) => ({
        ...row,
        referrer_name: row.referrer?.full_name ?? null,
        referrer: undefined,
      })) as SuccessStory[];

      return stories;
    });
  }

  async getMyReferrerUpgradeRequest(): Promise<ApiResponse<any | null>> {
    return wrapAsync(async () => {
      const session = await getSession();
      if (!session?.user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('referrer_upgrade_requests')
        .select('*')
        .eq('user_id', session.user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) {
        if (error.code === '42P01' || error.message?.includes('does not exist')) {
          console.warn('Table "referrer_upgrade_requests" does not exist in the database. Please execute SQL migration 001 in your Supabase dashboard.');
          return null;
        }
        if (error.code !== 'PGRST116') throw error;
      }
      return data;
    });
  }

  async getReferrerUpgradeRequestsForAdmin(status?: string): Promise<ApiResponse<any[]>> {
    return wrapAsync(async () => {
      const session = await getSession();
      if (!session?.user) throw new Error('Not authenticated');

      const profile = await getProfile(session.user.id);
      if (profile?.role !== 'admin') {
        throw new Error('Admin access required');
      }

      let query = supabase
        .from('referrer_upgrade_requests')
        .select('*')
        .order('created_at', { ascending: false });

      if (status) {
        query = query.eq('status', status);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    });
  }

  async approveReferrerUpgradeRequest(requestId: number): Promise<ApiResponse<any>> {
    return wrapAsync(async () => {
      const session = await getSession();
      if (!session?.user) throw new Error('Not authenticated');

      const adminProfile = await getProfile(session.user.id);
      if (adminProfile?.role !== 'admin') {
        throw new Error('Admin access required');
      }

      const { data: request, error: fetchError } = await supabase
        .from('referrer_upgrade_requests')
        .select('*')
        .eq('id', requestId)
        .single();
      if (fetchError) throw fetchError;
      if (!request || request.status !== 'pending') {
        throw new Error('Request not found or already processed');
      }

      const now = new Date().toISOString();

      // Stamp a REFFJR code for JS→WP upgrade path (best-effort).
      let reffjrCode: string | null = null;
      try {
        const { data: existingProfile } = await supabase
          .from('profiles')
          .select('user_code')
          .eq('id', request.user_id)
          .single();

        // Only stamp REFFJR if the user had a REEFJS code (proving JS→WP path).
        // If they somehow have no code yet, still give them a REFFJR code.
        if (!existingProfile?.user_code || existingProfile.user_code.startsWith('REEFJS')) {
          const { data: codeData } = await supabase
            .rpc('generate_user_code', { p_prefix: 'REFFJR' });
          reffjrCode = codeData ?? null;
        }
      } catch (codeErr) {
        console.warn('approveReferrerUpgradeRequest: REFFJR code generation skipped', codeErr);
      }

      // Merge REFFJR code into the profile update if we got one.
      const profileUpdatePayload: any = {
        role: 'referrer',
        company_name: request.company_name,
        office_email: request.company_email,
        updated_at: now,
      };
      if (reffjrCode) {
        profileUpdatePayload.user_code = reffjrCode;
      }

      const { error: profileError } = await supabase
        .from('profiles')
        .update(profileUpdatePayload)
        .eq('id', request.user_id);
      if (profileError) throw profileError;

      const { data, error } = await supabase
        .from('referrer_upgrade_requests')
        .update({
          status: 'approved',
          reviewed_at: now,
          reviewed_by: session.user.id,
          updated_at: now,
        } as any)
        .eq('id', requestId)
        .select()
        .single();
      if (error) throw error;

      // Best-effort: cascade is_approved on linked success story
      try {
        await supabase
          .from('success_stories')
          .update({ is_approved: true, updated_at: now })
          .eq('upgrade_request_id', requestId);
      } catch (storyErr: any) {
        if (storyErr?.code !== '42P01') {
          console.warn('Could not approve success story:', storyErr);
        }
      }

      return data;
    });
  }

  async rejectReferrerUpgradeRequest(requestId: number, adminNotes?: string): Promise<ApiResponse<any>> {
    return wrapAsync(async () => {
      const session = await getSession();
      if (!session?.user) throw new Error('Not authenticated');

      const adminProfile = await getProfile(session.user.id);
      if (adminProfile?.role !== 'admin') {
        throw new Error('Admin access required');
      }

      const now = new Date().toISOString();
      const { data, error } = await supabase
        .from('referrer_upgrade_requests')
        .update({
          status: 'rejected',
          reviewed_at: now,
          reviewed_by: session.user.id,
          admin_notes: adminNotes || null,
          updated_at: now,
        } as any)
        .eq('id', requestId)
        .eq('status', 'pending')
        .select()
        .single();
      if (error) throw error;

      // Best-effort: cascade is_approved on linked success story
      try {
        await supabase
          .from('success_stories')
          .update({ is_approved: false, updated_at: now })
          .eq('upgrade_request_id', requestId);
      } catch (storyErr: any) {
        if (storyErr?.code !== '42P01') {
          console.warn('Could not reject success story:', storyErr);
        }
      }

      return data;
    });
  }
  // ── Service Configurations ─────────────────────────────

  async upsertServiceConfiguration(payload: {
    service_id: string;
    availability: Record<string, { id: string; startTime: string; endTime: string }[]>;
    referral_call_duration?: number | null;
    slot_duration_minutes?: number | null;
    price?: number | null;
    is_active?: boolean;
  }): Promise<ApiResponse<any>> {
    return wrapAsync(async () => {
      const session = await getSession();
      if (!session?.user) throw new Error('Not authenticated');

      // Price rules.
      //
      // Resume review is platform-fixed and professionals cannot change it.
      // Referral sessions and mock interviews are priced by the professional,
      // and the platform must never substitute a figure on their behalf: the
      // amount actually charged is resolved server-side from this very row
      // (resolve_service_price_paise), so an invented price here becomes a real
      // mischarge. A null price is allowed and means "not priced yet" — the
      // marketplace filters those out rather than listing them.
      let finalPrice = payload.price ?? null;
      if (payload.service_id === 'resume-review') {
        finalPrice = PLATFORM_FIXED_SERVICE_PRICES['resume-review'];
      } else if (finalPrice !== null) {
        if (finalPrice < MIN_SERVICE_PRICE) finalPrice = MIN_SERVICE_PRICE;
        if (finalPrice > MAX_SERVICE_PRICE) finalPrice = MAX_SERVICE_PRICE;
      }

      // Resume Review is always on — no client bug can ever disable it.
      const finalIsActive =
        payload.service_id === 'resume-review' ? true : payload.is_active ?? true;

      const { data, error } = await supabase
        .from('service_configurations')
        .upsert(
          {
            user_id: session.user.id,
            service_id: payload.service_id,
            availability: payload.availability,
            referral_call_duration: payload.referral_call_duration ?? null,
            slot_duration_minutes: payload.slot_duration_minutes ?? null,
            price: finalPrice,
            is_active: finalIsActive,
            updated_at: new Date().toISOString(),
          } as any,
          { onConflict: 'user_id,service_id' }
        )
        .select()
        .single();
      if (error) throw error;

      invalidateCachePrefix('service-configs:');
      invalidateCachePrefix('service-configs-referrer:');
      invalidateCachePrefix('my-service-configs:');
      invalidateCacheKey('referrers');
      try {
        const { invalidateMarketplaceCache } = await import('../../features/bookings/utils/marketplaceCache');
        invalidateMarketplaceCache();
      } catch {
        // Optional marketplace cache invalidation.
      }

      return data;
    });
  }

  async getMyServiceConfigurations(): Promise<ApiResponse<any[]>> {
    const session = await getSession();
    if (!session?.user) {
      return { success: false, error: 'Not authenticated', status: 401 };
    }
    return wrapAsync(
      async () => {
        const { data, error } = await supabase
          .from('service_configurations')
          .select('*')
          .eq('user_id', session.user.id)
          .order('service_id');
        if (error) throw error;
        return data || [];
      },
      { key: buildCacheKey(['my-service-configs', session.user.id]) }
    );
  }

  async getServiceConfigurationsForReferrer(referrerUserId: string): Promise<ApiResponse<any[]>> {
    return wrapAsync(
      async () => {
        const { data, error } = await supabase
          .from('service_configurations')
          .select('*')
          .eq('user_id', referrerUserId)
          .eq('is_active', true)
          .order('service_id');
        if (error) throw error;
        return data || [];
      },
      // Short TTL: another user's browser tab has no way to know when a
      // professional flips a service on/off, so this cache must expire
      // quickly to avoid showing stale service availability.
      { key: buildCacheKey(['service-configs-referrer', referrerUserId]), ttlMs: 20_000 }
    );
  }

  async getServiceConfigurationsForService(serviceId: string): Promise<ApiResponse<any[]>> {
    return wrapAsync(
      async () => {
        const { data, error } = await supabase
          .from('service_configurations')
          .select('*')
          .eq('service_id', serviceId)
          .or('is_active.eq.true,is_active.is.null');
        if (error) throw error;
        return (data || []).filter((row: any) => row.is_active !== false);
      },
      // Short TTL: another user's browser tab has no way to know when a
      // professional flips a service on/off, so this cache must expire
      // quickly to avoid showing stale service availability.
      { key: buildCacheKey(['service-configs', serviceId]), ttlMs: 20_000 }
    );
  }

  // ── Shared request notes ───────────────────────────────

  /**
   * Read the note thread for a request. RLS restricts this to the requester
   * and the referrer on that request, so no extra ownership check is needed.
   */
  async getRequestNotes(
    service: RequestNoteService,
    requestId: number
  ): Promise<ApiResponse<RequestNoteRecord[]>> {
    return wrapAsync(async () => {
      const session = await getSession();
      if (!session?.user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('request_notes')
        .select('*')
        .eq('service', service)
        .eq('request_id', requestId)
        .order('created_at', { ascending: true });
      if (error) throw error;

      const rows = (data || []) as RequestNoteRecord[];

      const authorIds = Array.from(new Set(rows.map((r) => r.author_id).filter(Boolean)));
      const profileMap = new Map<string, Profile>();
      if (authorIds.length > 0) {
        const { data: profiles, error: profileError } = await supabase
          .from('profiles')
          .select('id, full_name, profile_picture, role, company_name, designation, user_code')
          .in('id', authorIds);
        if (profileError) {
          console.warn('Request note author profile lookup failed', profileError);
        }
        (profiles || []).forEach((p: any) => profileMap.set(p.id, p as Profile));
      }

      return rows.map((row) => ({
        ...row,
        author_profile: profileMap.get(row.author_id) || null,
      }));
    });
  }

  async addRequestNote(payload: {
    service: RequestNoteService;
    request_id: number;
    body: string;
    is_action_item?: boolean;
  }): Promise<ApiResponse<RequestNoteRecord>> {
    return wrapAsync(async () => {
      const session = await getSession();
      if (!session?.user) throw new Error('Not authenticated');

      const body = payload.body.trim();
      if (!body) throw new Error('Note cannot be empty');
      if (body.length > 2000) throw new Error('Note is too long (2000 character limit)');

      const { data, error } = await supabase
        .from('request_notes')
        .insert({
          service: payload.service,
          request_id: payload.request_id,
          author_id: session.user.id,
          body,
          is_action_item: payload.is_action_item ?? false,
        } as any)
        .select()
        .single();
      if (error) throw error;
      return data as RequestNoteRecord;
    });
  }

  /**
   * Tick an action item off. Goes through an RPC so either party can toggle it
   * without being granted UPDATE on notes they didn't author.
   */
  async toggleRequestActionItem(
    noteId: number,
    isDone: boolean
  ): Promise<ApiResponse<RequestNoteRecord>> {
    return wrapAsync(async () => {
      const { data, error } = await supabase.rpc('toggle_request_action_item', {
        p_note_id: noteId,
        p_is_done: isDone,
      });
      if (error) throw error;
      return data as RequestNoteRecord;
    });
  }

  async deleteRequestNote(noteId: number): Promise<ApiResponse<null>> {
    return wrapAsync(async () => {
      const { error } = await supabase.from('request_notes').delete().eq('id', noteId);
      if (error) throw error;
      return null;
    });
  }

  async submitReferralSessionRating(payload: {
    sessionId: number;
    rating: number;
    wouldBookAgain: boolean;
    reviewText?: string;
    referralSubmitted?: string;
  }): Promise<ApiResponse<any>> {
    return wrapAsync(async () => {
      const session = await getSession();
      if (!session?.user) throw new Error('Not authenticated');

      // Try secure database RPC first
      const { data: rpcData, error: rpcError } = await supabase.rpc('submit_referral_session_rating', {
        p_session_id: payload.sessionId,
        p_rating: payload.rating,
        p_would_book_again: payload.wouldBookAgain,
        p_review_text: payload.reviewText || null,
        p_referral_submitted: payload.referralSubmitted || null,
      });

      if (!rpcError && rpcData) {
        return rpcData;
      }

      // Fallback: Direct table operations
      try {
        // 1. Fetch referral session to get request_id (+ referrer for event log)
        const { data: refSession } = await supabase
          .from('referral_sessions')
          .select('request_id, referrer_id')
          .eq('id', payload.sessionId)
          .maybeSingle();

        // 2. Insert rating record
        const { data: ratingData, error: ratingError } = await supabase
          .from('referral_session_ratings')
          .upsert({
            session_id: payload.sessionId,
            rating: payload.rating,
            would_book_again: payload.wouldBookAgain,
            review_text: payload.reviewText || null,
            referral_submitted: payload.referralSubmitted || null,
          })
          .select()
          .maybeSingle();

        if (ratingError) {
          // If RLS blocked insert (e.g. test / demo session ownership mismatch), log and return clean local rating object
          if (ratingError.message?.toLowerCase().includes('row-level security') || ratingError.code === '42501') {
            console.warn('Session rating RLS restricted for test session, recording local rating state', ratingError);
            return {
              session_id: payload.sessionId,
              rating: payload.rating,
              would_book_again: payload.wouldBookAgain,
              review_text: payload.reviewText || null,
              referral_submitted: payload.referralSubmitted || null,
              is_local: true,
            };
          }
          throw ratingError;
        }

        // 3. Update session status & is_rated
        await supabase
          .from('referral_sessions')
          .update({
            status: 'completed',
            is_rated: true,
            updated_at: new Date().toISOString(),
          })
          .eq('id', payload.sessionId);

        // 4. Update request status to completed
        if (refSession?.request_id) {
          await supabase
            .from('referral_requests')
            .update({
              status: 'completed',
              updated_at: new Date().toISOString(),
            })
            .eq('id', refSession.request_id);
        }

        if (refSession?.referrer_id) {
          logEvent('rate', 'referrer', refSession.referrer_id, {
            rating: payload.rating,
            service: 'referral-session',
          });
        }

        return ratingData || { success: true };
      } catch (err: any) {
        if (err?.message?.toLowerCase().includes('row-level security') || err?.code === '42501') {
          console.warn('Handling demo/test session rating gracefully', err);
          return {
            session_id: payload.sessionId,
            rating: payload.rating,
            would_book_again: payload.wouldBookAgain,
            review_text: payload.reviewText || null,
            referral_submitted: payload.referralSubmitted || null,
            is_local: true,
          };
        }
        throw err;
      }
    });
  }

  async submitMockInterviewSessionRating(payload: {
    sessionId: number;
    rating: number;
    wouldBookAgain: boolean;
    reviewText?: string;
  }): Promise<ApiResponse<any>> {
    return wrapAsync(async () => {
      const session = await getSession();
      if (!session?.user) throw new Error('Not authenticated');

      // Try secure database RPC first
      const { data: rpcData, error: rpcError } = await supabase.rpc('submit_mock_interview_session_rating', {
        p_session_id: payload.sessionId,
        p_rating: payload.rating,
        p_would_book_again: payload.wouldBookAgain,
        p_review_text: payload.reviewText || null,
      });

      if (!rpcError && rpcData) {
        return rpcData;
      }

      // Fallback: Direct table operations
      try {
        const { data: mockSession } = await supabase
          .from('mock_interview_sessions')
          .select('request_id, referrer_id')
          .eq('id', payload.sessionId)
          .maybeSingle();

        const { data: ratingData, error: ratingError } = await supabase
          .from('mock_interview_session_ratings')
          .upsert({
            session_id: payload.sessionId,
            rating: payload.rating,
            would_book_again: payload.wouldBookAgain,
            review_text: payload.reviewText || null,
          })
          .select()
          .maybeSingle();

        if (ratingError) {
          if (ratingError.message?.toLowerCase().includes('row-level security') || ratingError.code === '42501') {
            console.warn('Mock interview rating RLS restricted for test session, recording local rating state', ratingError);
            return {
              session_id: payload.sessionId,
              rating: payload.rating,
              would_book_again: payload.wouldBookAgain,
              review_text: payload.reviewText || null,
              is_local: true,
            };
          }
          throw ratingError;
        }

        await supabase
          .from('mock_interview_sessions')
          .update({
            status: 'completed',
            is_rated: true,
            updated_at: new Date().toISOString(),
          })
          .eq('id', payload.sessionId);

        if (mockSession?.request_id) {
          await supabase
            .from('mock_interview_requests')
            .update({
              status: 'completed',
              updated_at: new Date().toISOString(),
            })
            .eq('id', mockSession.request_id);
        }

        if (mockSession?.referrer_id) {
          logEvent('rate', 'referrer', mockSession.referrer_id, {
            rating: payload.rating,
            service: 'interview-mock',
          });
        }

        return ratingData || { success: true };
      } catch (err: any) {
        if (err?.message?.toLowerCase().includes('row-level security') || err?.code === '42501') {
          console.warn('Handling demo/test mock rating gracefully', err);
          return {
            session_id: payload.sessionId,
            rating: payload.rating,
            would_book_again: payload.wouldBookAgain,
            review_text: payload.reviewText || null,
            is_local: true,
          };
        }
        throw err;
      }
    });
  }

  async submitResumeReviewRating(payload: {
    requestId: number;
    rating: number;
    wouldBookAgain: boolean;
    reviewText?: string;
  }): Promise<ApiResponse<any>> {
    return wrapAsync(async () => {
      const session = await getSession();
      if (!session?.user) throw new Error('Not authenticated');

      // 1. Insert rating record
      const { data: ratingData, error: ratingError } = await supabase
        .from('resume_review_ratings')
        .insert({
          request_id: payload.requestId,
          rating: payload.rating,
          would_book_again: payload.wouldBookAgain,
          review_text: payload.reviewText || null,
        })
        .select()
        .single();
      if (ratingError) throw ratingError;

      // 2. Update request status to is_rated = true
      const { error: requestError } = await supabase
        .from('resume_review_requests')
        .update({
          is_rated: true,
          updated_at: new Date().toISOString(),
        })
        .eq('id', payload.requestId);
      if (requestError) throw requestError;

      const { data: reviewReq } = await supabase
        .from('resume_review_requests')
        .select('referrer_id')
        .eq('id', payload.requestId)
        .maybeSingle();
      if (reviewReq?.referrer_id) {
        logEvent('rate', 'referrer', reviewReq.referrer_id, {
          rating: payload.rating,
          service: 'resume-review',
        });
      }

      return ratingData;
    });
  }

  // ── Platform stats (honest social proof) ───────────────

  async getPlatformStats(): Promise<ApiResponse<{
    resumes_graded_week: number;
    sessions_completed_total: number;
    active_referrers: number;
  }>> {
    return wrapAsync(
      async () => {
        const { data, error } = await supabase.rpc('get_platform_stats');
        if (error) throw error;
        const row = Array.isArray(data) ? data[0] : data;
        return {
          resumes_graded_week: Number(row?.resumes_graded_week || 0),
          sessions_completed_total: Number(row?.sessions_completed_total || 0),
          active_referrers: Number(row?.active_referrers || 0),
        };
      },
      { key: 'platform-stats' }
    );
  }

  // ── Health Check ────────────────────────────────────────

  async healthCheck(): Promise<ApiResponse<{ status: string; message: string }>> {
    return wrapAsync(async () => {
      // Quick check: can we reach Supabase?
      const { error } = await supabase.from('categories').select('id').limit(1);
      if (error) throw error;
      return { status: 'ok', message: 'Supabase connection healthy' };
    });
  }

  /**
   * Erases the account and every artifact it owns, then records the outcome.
   *
   * The previous implementation only swept `resume-reviews/{userId}` and
   * `resume-reviews/{userId}/referrals`. Weekly Drop resumes are written to
   * `resume-reviews/referral-drops/{userId}_{timestamp}.{ext}` and voice pitches
   * live in a separate `voice-pitches` bucket, so both survived deletion. Those
   * paths are only derivable from application rows, so the server enumerates them
   * and the exact objects are removed through the Storage API, which is the only
   * path that purges the physical file.
   */
  async deleteAccount(): Promise<ApiResponse<{ success: boolean }>> {
    return wrapAsync(async () => {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) throw new Error('Not authenticated');

      const userId = user.id;
      const failureCodes = new Set<string>();
      let requested = 0;
      let deleted = 0;

      // 1. Ask the server for the exact objects this account owns.
      const pathsByBucket = new Map<string, Set<string>>();
      const addPath = (bucket: string, path: string | null | undefined) => {
        const clean = (path || '').trim().replace(/^\/+/, '');
        if (!clean) return;
        if (!pathsByBucket.has(bucket)) pathsByBucket.set(bucket, new Set());
        pathsByBucket.get(bucket)!.add(clean);
      };

      const { data: ownedObjects, error: manifestError } = await supabase
        .rpc('list_own_deletable_storage_objects');
      if (manifestError) {
        failureCodes.add('manifest_unavailable');
        console.error('Could not enumerate owned storage objects:', manifestError.message);
      }
      (ownedObjects || []).forEach((row: { bucket_id: string; object_path: string }) => {
        addPath(row.bucket_id, row.object_path);
      });

      // 2. Sweep the legacy per-user prefixes the manifest cannot see, plus the
      //    Weekly Drop and voice-pitch prefixes that were previously missed.
      const prefixSweeps: Array<{ bucket: string; prefix: string; ownedOnly: boolean }> = [
        { bucket: 'resume-reviews', prefix: userId, ownedOnly: false },
        { bucket: 'resume-reviews', prefix: `${userId}/referrals`, ownedOnly: false },
        { bucket: 'resume-reviews', prefix: 'referral-drops', ownedOnly: true },
        { bucket: 'voice-pitches', prefix: userId, ownedOnly: false },
        { bucket: 'voice-pitches', prefix: 'referral-drops', ownedOnly: true },
      ];

      for (const sweep of prefixSweeps) {
        try {
          const { data: entries, error: listError } = await supabase
            .storage
            .from(sweep.bucket)
            .list(sweep.prefix, { limit: 1000 });
          if (listError || !entries) continue;
          entries.forEach((entry) => {
            // Directory placeholders have no id; skip them rather than deleting a prefix.
            if (!entry.id) return;
            // Shared prefixes hold other candidates' files. Only this account's
            // `{userId}_{timestamp}` objects may be removed.
            if (sweep.ownedOnly && !entry.name.startsWith(`${userId}_`)) return;
            addPath(sweep.bucket, `${sweep.prefix}/${entry.name}`);
          });
        } catch {
          /* A missing bucket is not a deletion failure. */
        }
      }

      // 3. Remove the objects. Storage API removal is what purges the physical file.
      for (const [bucket, paths] of pathsByBucket.entries()) {
        const list = Array.from(paths);
        requested += list.length;
        for (let i = 0; i < list.length; i += 100) {
          const batch = list.slice(i, i + 100);
          try {
            const { data: removedRows, error: removeError } = await supabase
              .storage
              .from(bucket)
              .remove(batch);
            if (removeError) {
              failureCodes.add(`remove_failed:${bucket}`);
              console.error(`Failed to remove objects from ${bucket}:`, removeError.message);
              continue;
            }
            deleted += (removedRows || []).length;
          } catch (err: any) {
            failureCodes.add(`remove_threw:${bucket}`);
            console.error(`Storage removal threw for ${bucket}:`, err?.message || err);
          }
        }
      }

      // 4. Record reconciliation before the account row disappears. This is the
      //    only evidence that physical erasure was attempted and how far it got.
      const failed = Math.max(requested - deleted, 0);
      const { error: reconError } = await supabase.rpc('record_own_deletion_reconciliation', {
        p_requested: requested,
        p_deleted: deleted,
        p_failed: failed,
        p_failure_codes: Array.from(failureCodes),
      });
      if (reconError) {
        console.error('Could not record deletion reconciliation:', reconError.message);
      }

      // 5. Drop the account. Database rows cascade; storage was handled above.
      const { error } = await supabase.rpc('delete_own_user');
      if (error) throw error;
      return { success: true };
    });
  }

  async getSuccessStoryForUpgradeRequest(upgradeRequestId: number): Promise<ApiResponse<SuccessStory | null>> {
    return wrapAsync(async () => {
      const session = await getSession();
      if (!session?.user) throw new Error('Not authenticated');

      // Verify admin
      const adminProfile = await getProfile(session.user.id);
      if (adminProfile?.role !== 'admin') {
        throw new Error('Admin access required');
      }

      const { data, error } = await supabase
        .from('success_stories')
        .select('*')
        .eq('upgrade_request_id', upgradeRequestId)
        .maybeSingle();

      if (error) {
        if (error.code === '42P01') {
          console.warn('Table "success_stories" does not exist.');
          return null;
        }
        throw error;
      }

      return (data as SuccessStory) ?? null;
    });
  }

  async exportUserData(): Promise<ApiResponse<any>> {
    return wrapAsync(async () => {
      const session = await getSession();
      if (!session?.user) throw new Error('Not authenticated');
      
      const userId = session.user.id;
      
      const [profileRes, onboardingRes, referralRes, resumeRes, mockRes] = await Promise.all([
        supabase.from('profiles').select('*').eq('id', userId).maybeSingle(),
        supabase.from('user_onboarding').select('*').eq('user_id', userId).maybeSingle(),
        supabase.from('referral_requests').select('*').eq('requester_id', userId),
        supabase.from('resume_review_requests').select('*').eq('requester_id', userId),
        supabase.from('mock_interview_requests').select('*').eq('requester_id', userId)
      ]);
      
      return {
        exported_at: new Date().toISOString(),
        profile: profileRes.data || null,
        onboarding: onboardingRes.data || null,
        referral_requests: referralRes.data || [],
        resume_review_requests: resumeRes.data || [],
        mock_interview_requests: mockRes.data || []
      };
    });
  }

  async getSuccessStoriesForReferrer(referrerId: string): Promise<ApiResponse<SuccessStory[]>> {
    return wrapAsync(async () => {
      const { data, error } = await supabase
        .from('success_stories')
        .select('*, author:user_id(full_name)')
        .eq('referrer_id', referrerId)
        .eq('is_approved', true)
        .order('created_at', { ascending: false });

      if (error) {
        if (error.code === '42P01') {
          console.warn('Table "success_stories" does not exist.');
          return [];
        }
        throw error;
      }

      const stories = (data || []).map((row: any) => ({
        ...row,
        user_name: row.author?.full_name ?? null,
        author: undefined,
      })) as SuccessStory[];

      return stories;
    });
  }

  async getCompletedBookingReferrers(): Promise<ApiResponse<{ id: string; name: string }[]>> {
    return wrapAsync(async () => {
      const session = await getSession();
      if (!session?.user) throw new Error('Not authenticated');

      const uid = session.user.id;

      // Query all three request tables for completed bookings where this user is the requester
      const [referralRes, resumeRes, mockRes] = await Promise.allSettled([
        supabase
          .from('referral_requests')
          .select('referrer_id, profiles!referrer_id(full_name)')
          .eq('requester_id', uid)
          .eq('status', 'completed'),
        supabase
          .from('resume_review_requests')
          .select('referrer_id, profiles!referrer_id(full_name)')
          .eq('requester_id', uid)
          .eq('status', 'feedback_delivered'),
        supabase
          .from('mock_interview_requests')
          .select('referrer_id, profiles!referrer_id(full_name)')
          .eq('requester_id', uid)
          .eq('status', 'completed'),
      ]);

      const seen = new Set<string>();
      const referrers: { id: string; name: string }[] = [];

      const processRows = (rows: any[]) => {
        for (const row of rows) {
          if (row.referrer_id && !seen.has(row.referrer_id)) {
            seen.add(row.referrer_id);
            const name = (row.profiles as any)?.full_name ?? 'Unknown';
            referrers.push({ id: row.referrer_id, name });
          }
        }
      };

      if (referralRes.status === 'fulfilled' && referralRes.value.data) {
        processRows(referralRes.value.data);
      }
      if (resumeRes.status === 'fulfilled' && resumeRes.value.data) {
        processRows(resumeRes.value.data);
      }
      if (mockRes.status === 'fulfilled' && mockRes.value.data) {
        processRows(mockRes.value.data);
      }

      return referrers;
    });
  }
}

// ─── Singleton + exports ────────────────────────────────

export const apiClient = new ApiClient();

export { clearApiCache } from './apiCache';

export const isAuthenticated = (): boolean => {
  return !!localStorage.getItem('access_token');
};

export const getStoredTokens = () => {
  return {
    accessToken: localStorage.getItem('access_token'),
    refreshToken: localStorage.getItem('refresh_token'),
  };
};

export const storeTokens = (tokens: AuthTokens) => {
  localStorage.setItem('access_token', tokens.access_token);
  localStorage.setItem('refresh_token', tokens.refresh_token);
};

export const clearTokens = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
};

export const logout = async () => {
  try {
    await authSignOut();
  } catch {
    // Ignore sign-out errors during local cleanup.
  }
  clearApiCache();
  try {
    const { invalidateMarketplaceCache } = await import('../../features/bookings/utils/marketplaceCache');
    invalidateMarketplaceCache();
  } catch {
    // Marketplace cache is optional during logout.
  }
  clearTokens();
  localStorage.removeItem('user');
  localStorage.removeItem('activeDashboardMode');
  localStorage.removeItem('referrer_upgrade_request');
  window.location.assign(`${window.location.origin}/login`);
};

export default apiClient;
